import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useBranch } from '../contexts/BranchContext';
import { useLanguage } from '../contexts/LanguageContext';
import Chart from 'chart.js/auto';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import * as XLSX from 'xlsx';
import { useCurrency } from '../contexts/CurrencyContext';
import { usePlanFeatures } from '../hooks/usePlanFeatures';
import PlanRenewalModal from '../components/PlanRenewalModal';
import { saveToStore, getFromStore } from '../utils/localDb';

// Register a custom plugin to show data labels on top of charts for world-class clarity
Chart.register({
    id: 'datalabels',
    afterDatasetsDraw(chart) {
        const { ctx } = chart;
        const enabled = chart.options.plugins?.datalabels?.display !== false;
        if (!enabled) return;

        ctx.save();
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.font = 'bold 12px sans-serif';

        chart.data.datasets.forEach((dataset, i) => {
            const meta = chart.getDatasetMeta(i);
            if (meta.hidden) return;

            meta.data.forEach((element, index) => {
                const dataValue = dataset.data[index];
                if (dataValue === 0 || dataValue === null || dataValue === undefined) return;

                let label = dataValue;
                if (typeof dataValue === 'number' && dataValue >= 1000) {
                    label = dataValue >= 1000000 ? (dataValue / 1000000).toFixed(1) + 'M' : (dataValue / 1000).toFixed(1) + 'K';
                }

                const pos = element.tooltipPosition ? element.tooltipPosition() : { x: element.x, y: element.y };
                let x = pos.x;
                let y = pos.y;

                // Shift Y for Bar/Line to ensure label is ABOVE the element
                if (chart.config.type === 'bar' || chart.config.type === 'line') {
                    y -= 12;
                }

                const textMetrics = ctx.measureText(label);
                const textWidth = textMetrics.width;
                const textHeight = 12;
                const paddingX = 8;
                const paddingY = 4;

                const boxW = textWidth + paddingX * 2;
                const boxH = textHeight + paddingY * 2;

                // Final safety check to keep labels inside canvas
                const finalY = Math.max(boxH / 2 + 2, y);
                const finalX = Math.min(Math.max(boxW / 2 + 2, x), chart.width - boxW / 2 - 2);

                const boxX = finalX - boxW / 2;
                const boxY = finalY - boxH / 2;

                // Draw ultra-clear pill background
                ctx.fillStyle = '#ffffff';
                ctx.shadowColor = 'rgba(0,0,0,0.4)';
                ctx.shadowBlur = 6;
                ctx.beginPath();
                if (ctx.roundRect) {
                    ctx.roundRect(boxX, boxY, boxW, boxH, 8);
                } else {
                    ctx.rect(boxX, boxY, boxW, boxH);
                }
                ctx.fill();
                ctx.shadowBlur = 0;

                // Draw solid black text
                ctx.fillStyle = '#000000';
                ctx.fillText(label, finalX, finalY);
            });
        });
        ctx.restore();
    }
});

function AnalyticsPage() {
    const { token, logout, currentUser, isLoading: authLoading } = useAuth();
    const navigate = useNavigate();
    const { currency, setCurrency: setCurrentCurrency, formatAmount } = useCurrency();
    const [timePeriod, setTimePeriod] = useState('daily');
    const [analyticsData, setAnalyticsData] = useState(null);
    const [previousPeriodData, setPreviousPeriodData] = useState(null);
    const [branchComparisonData, setBranchComparisonData] = useState(null); // New State
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('overview');
    const [companyInfo, setCompanyInfo] = useState(null);
    const planFeatures = usePlanFeatures(companyInfo);
    const canAccessPremium = planFeatures.isPremium || planFeatures.isTrial;
    const [isProratedUpgradeOpen, setIsProratedUpgradeOpen] = useState(false);

    // Smart upgrade handler: paid Starter users see proration modal; others go to pricing page
    const [confirmModal, setConfirmModal] = useState({ show: false, message: '', onConfirm: () => {} });
    const handleUpgradeClick = () => {
        setConfirmModal({
            show: true,
            title: 'Upgrade Required',
            message: 'This feature is not available in the Starter plan. You need to upgrade the plan to access this feature. Do you want to upgrade the plan?',
            confirmText: 'Upgrade Plan',
            confirmClass: 'btn btn-primary flex-1 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-lg',
            cancelClass: 'btn btn-secondary flex-1 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold rounded-lg',
            onConfirm: () => {
                setConfirmModal({ show: false, message: '', onConfirm: () => {} });
                setIsProratedUpgradeOpen(true);
            }
        });
    };
    const [reportsSubSection, setReportsSubSection] = useState('excel'); // 'excel' or 'ai'
    const { branches } = useBranch();
    const [selectedBranch, setSelectedBranchState] = useState(() => {
        const saved = localStorage.getItem('selectedBranch_analytics');
        return saved ? (saved === 'null' ? null : parseInt(saved, 10)) : null;
    });
    const setSelectedBranch = (val) => {
        setSelectedBranchState(val);
        localStorage.setItem('selectedBranch_analytics', val === null ? 'null' : val.toString());
    };
    // Auto-select the only branch when there's exactly one
    useEffect(() => {
        if (branches && branches.length === 1 && !selectedBranch) {
            setSelectedBranch(branches[0].id);
        }
    }, [branches]); // eslint-disable-line react-hooks/exhaustive-deps
    const { t, language } = useLanguage();
    const [showBranchDropdown, setShowBranchDropdown] = useState(false);
    const [customStart, setCustomStart] = useState('');
    const [customEnd, setCustomEnd] = useState('');

    useEffect(() => {
        let isMounted = true;
        const fetchCompanyInfo = async () => {
            if (authLoading) return;
            try {
                const API_URL = window.location.hostname === 'localhost' || window.location.hostname.endsWith('.localhost') || window.location.hostname === '127.0.0.1'
                    ? 'http://localhost:5000'
                    : (process.env.REACT_APP_API_URL || 'https://dineflowbackend.onrender.com');

                const url = token
                    ? `${API_URL}/api/company/profile`
                    : `${API_URL}/api/company/public`;

                const options = token
                    ? { headers: { 'Authorization': `Bearer ${token}` } }
                    : {};

                const res = await fetch(url, options);
                const json = await res.json();
                if (isMounted && json.success && json.data) {
                    setCompanyInfo(json.data);
                }
            } catch (err) {
                console.error("Failed to fetch company info", err);
            }
        };
        fetchCompanyInfo();
        return () => { isMounted = false; };
    }, [token, authLoading]);

    // Click outside to close dropdowns
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!event.target.closest('.language-dropdown-container') && !event.target.closest('.branch-dropdown-container')) {
                setShowBranchDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Refs for chart canvases
    const revenueChartRef = useRef(null);
    const ordersChartRef = useRef(null);
    const categoryChartRef = useRef(null);
    const paymentChartRef = useRef(null);
    const itemsChartRef = useRef(null);
    const customerChartRef = useRef(null);
    const hourlyChartRef = useRef(null);
    const tableChartRef = useRef(null);
    const branchRevenueChartRef = useRef(null); // New Ref
    const branchOrdersChartRef = useRef(null); // New Ref

    // Refs for chart instances to destroy them properly
    const revenueChartInstance = useRef(null);
    const ordersChartInstance = useRef(null);
    const categoryChartInstance = useRef(null);
    const paymentChartInstance = useRef(null);
    const itemsChartInstance = useRef(null);
    const customerChartInstance = useRef(null);
    const hourlyChartInstance = useRef(null);
    const tableChartInstance = useRef(null);
    const branchRevenueChartInstance = useRef(null); // New Ref
    const branchOrdersChartInstance = useRef(null); // New Ref

    const API_URL = window.location.hostname === 'localhost' || window.location.hostname.endsWith('.localhost') || window.location.hostname === '127.0.0.1'
        ? 'http://localhost:5000'
        : (process.env.REACT_APP_API_URL || 'https://dineflowbackend.onrender.com');

    const formatCurrency = useCallback((value) => {
        return formatAmount(value);
    }, [formatAmount]);

    // Function to download table data as CSV
    // const downloadTableData = useCallback(() => {
    //     if (!analyticsData?.tablePerformance) return;

    //     try {
    //         const headers = ['Table', 'Orders', 'Revenue', 'Avg Order Value'];
    //         const rows = analyticsData.tablePerformance.map(table => [
    //             table.table_name,
    //             table.total_orders,
    //             formatCurrency(table[`total_revenue_${currency.toLowerCase()}`], currency),
    //             formatCurrency(table[`avg_order_value_${currency.toLowerCase()}`], currency)
    //         ]);

    //         const csvContent = [
    //             headers.join(','),
    //             ...rows.map(row => row.join(','))
    //         ].join('\n');

    //         const blob = new Blob([csvContent], { type: 'text/csv' });
    //         const url = URL.createObjectURL(blob);
    //         const link = document.createElement('a');
    //         link.download = `table-performance-${timePeriod}-${currency}.csv`;
    //         link.href = url;
    //         document.body.appendChild(link);
    //         link.click();
    //         document.body.removeChild(link);
    //         URL.revokeObjectURL(url);
    //     } catch (error) {
    //         console.error("Error downloading table data:", error);
    //         alert(t('operationFailed') || "Failed to download table data.");
    //     }
    // }, [analyticsData, currency, timePeriod, t, formatCurrency]);

    // Function to download Excel Report
    const downloadExcelReport = () => {
        if (!analyticsData) {
            alert(t('noDataAvailable') || "No data available to download.");
            return;
        }

        try {
            const wb = XLSX.utils.book_new();

            // Helper to add sheet
            const addSheet = (data, name) => {
                if (data && data.length > 0) {
                    const ws = XLSX.utils.json_to_sheet(data);
                    XLSX.utils.book_append_sheet(wb, ws, name);
                }
            };

            addSheet([analyticsData.summary], "Summary");
            addSheet(analyticsData.revenueOrders, "Revenue & Orders");
            addSheet(analyticsData.topItems, "Top Items");
            addSheet(analyticsData.categories, "Categories");

            // Payment methods needs transformation
            if (analyticsData.paymentMethods) {
                const paymentData = Object.entries(analyticsData.paymentMethods).map(([method, count]) => ({ Method: method, Orders: count }));
                addSheet(paymentData, "Payment Methods");
            }

            addSheet(analyticsData.hourlyOrders, "Hourly Orders");
            addSheet(analyticsData.tablePerformance, "Table Performance");

            if (analyticsData.customerRetention) {
                addSheet([analyticsData.customerRetention], "Customer Retention");
            }

            XLSX.writeFile(wb, `Analytics_Report_${timePeriod}_${currency}.xlsx`);
        } catch (error) {
            console.error("Error downloading Excel report:", error);
            alert("Failed to generate Excel report. Please check the console for details.");
        }
    };

    // Function to download PDF Report
    const downloadPDFReport = async () => {
        const element = document.getElementById('analytics-dashboard-content');
        if (!element) {
            alert(t('operationFailed') || "Dashboard content not found.");
            return;
        }

        const originalCursor = document.body.style.cursor;
        document.body.style.cursor = 'wait';

        try {
            const canvas = await html2canvas(element, {
                scale: 2.5, // Slightly lower scale for faster processing but still ultra-clear
                useCORS: true,
                logging: false,
                allowTaint: false,
                backgroundColor: '#ffffff',
                height: element.scrollHeight, // FORCE FULL HEIGHT
                windowHeight: element.scrollHeight, // ENSURE VIEWPORT DOESN'T CLIFF IT
                onclone: (clonedDoc) => {
                    const clonedElement = clonedDoc.getElementById('analytics-dashboard-content');
                    if (clonedElement) {
                        // FORCE ABSOLUTE VISIBILITY FOR PDF
                        clonedElement.style.opacity = '1';
                        clonedElement.style.visibility = 'visible';
                        clonedElement.style.backgroundColor = '#ffffff';
                        clonedElement.style.display = 'block';
                        clonedElement.style.transform = 'none';
                        clonedElement.style.height = 'auto'; // Let it grow
                        clonedElement.style.maxHeight = 'none';
                        clonedElement.style.overflow = 'visible';

                        // Fix text colors that might be too light in capture
                        const allText = clonedElement.querySelectorAll('h1, h2, h3, h4, p, span, th, td');
                        allText.forEach(el => {
                            const style = window.getComputedStyle(el);
                            if (style.color.includes('rgba') || style.opacity < 1) {
                                el.style.color = '#1e293b'; // Force Slate 800
                                el.style.opacity = '1';
                            }
                        });

                        // Ensure titles are explicitly dark
                        const titles = clonedElement.querySelectorAll('.analytics-section-title, h3, h4');
                        titles.forEach(t => {
                            t.style.color = '#000000';
                            t.style.fontWeight = 'bold';
                        });

                        // Remove all transitions/animations (including keyframe-based animations like expand-in)
                        const animated = clonedElement.querySelectorAll('.fade-in, .transition-all, .transition-opacity, .expand-in');
                        animated.forEach(el => {
                            el.style.animation = 'none';
                            el.style.transition = 'none';
                            el.style.opacity = '1';
                            el.style.transform = 'none';
                        });

                        // Explicitly fix vertical layout and clipping issues for the metrics strip
                        const metricsStrip = clonedElement.querySelector('#metrics-pulse-strip');
                        if (metricsStrip) {
                            metricsStrip.style.overflow = 'visible';
                            metricsStrip.style.opacity = '1';
                            metricsStrip.style.transform = 'none';
                        }
                        const valueContainers = clonedElement.querySelectorAll('.text-2xl, .text-3xl');
                        valueContainers.forEach(val => {
                            val.style.lineHeight = '1.3';
                            val.style.display = 'inline-block';
                            val.style.overflow = 'visible';
                            val.style.paddingBottom = '3px';
                        });

                        // Ensure charts are high contrast
                        const charts = clonedElement.querySelectorAll('.analytics-chart-wrapper');
                        charts.forEach(c => {
                            c.style.opacity = '1';
                            c.style.padding = '20px';
                            c.style.backgroundColor = '#ffffff';
                        });
                    }
                }
            });

            const imgData = canvas.toDataURL('image/png');
            const pdfWidth = 210; // A4 width in mm
            const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

            // Create PDF with dynamic height to ensure FULL page is included
            const pdf = new jsPDF('p', 'mm', [pdfWidth, pdfHeight]);

            pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
            pdf.save(`Analytics_Full_Report_${timePeriod}.pdf`);
        } catch (err) {
            console.error("PDF Generation Error:", err);
            alert("Failed to generate PDF report. Please check the console for details.");
        } finally {
            document.body.style.cursor = originalCursor;
        }
    };

    // Function to download individual chart data
    const downloadChartData = (data, filename) => {
        if (!data) {
            alert(t('noDataAvailable') || "No data to download.");
            return;
        }
        try {
            const wb = XLSX.utils.book_new();
            // Handle object data (like payment methods)
            let sheetData = data;
            if (!Array.isArray(data) && typeof data === 'object') {
                sheetData = Object.entries(data).map(([k, v]) => ({ Label: k, Value: v }));
            } else if (!Array.isArray(data)) {
                sheetData = [data];
            }

            const ws = XLSX.utils.json_to_sheet(sheetData);
            XLSX.utils.book_append_sheet(wb, ws, "Data");
            XLSX.writeFile(wb, `${filename}.xlsx`);
        } catch (error) {
            console.error("Error downloading chart data:", error);
            alert("Failed to download chart data.");
        }
    };

    // Function to download chart image with proper background and values
    const downloadChart = (chartRef, filename) => {
        if (chartRef.current) {
            const canvas = chartRef.current;
            const tempCanvas = document.createElement('canvas');
            const tempCtx = tempCanvas.getContext('2d');

            // Set dimensions to match original canvas
            tempCanvas.width = canvas.width;
            tempCanvas.height = canvas.height;

            // Create a solid white background (crucial for visibility)
            tempCtx.fillStyle = '#ffffff';
            tempCtx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);

            // Draw the chart on top of the white background
            tempCtx.drawImage(canvas, 0, 0);

            const link = document.createElement('a');
            link.download = filename;
            link.href = tempCanvas.toDataURL('image/png', 1.0);
            link.click();
        }
    };

    // Fetch analytics data from API
    const fetchAnalyticsData = useCallback(async () => {
        if (!token) return;
        if (timePeriod === 'custom' && (!customStart || !customEnd)) return;
        setLoading(true);
        setError(null);

        const authFetch = async (url) => {
            const res = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
            if (res.status === 401) {
                logout();
                throw new Error('Unauthorized');
            }
            if (res.status === 403) {
                throw new Error('Forbidden');
            }
            return res;
        };

        const branchQuery = selectedBranch ? `&branch_id=${selectedBranch}` : '';
        const dateQuery = timePeriod === 'custom' && customStart && customEnd
            ? `&startDate=${customStart}&endDate=${customEnd}`
            : '';

        if (process.env.NODE_ENV === 'development') console.time('fetchAnalyticsData_total');

        try {
            // 1) Fetch summary immediately so the page can render quickly
            const summaryRes = await authFetch(`${API_URL}/api/analytics/summary?period=${timePeriod}&currency=${currency}${branchQuery}${dateQuery}`);
            if (!summaryRes.ok) throw new Error(`Summary API error: ${summaryRes.status}`);
            const summaryResponse = await summaryRes.json();
            const summary = summaryResponse.success ? summaryResponse.data : {};

            // Set summary early for quick paint
            setAnalyticsData(prev => ({ ...(prev || {}), summary }));
            setLoading(false);

            // 2) Fetch remaining datasets in parallel (background)
            const otherEndpoints = [
                ['revenueOrders', `${API_URL}/api/analytics/revenue-orders?period=${timePeriod}&currency=${currency}${branchQuery}${dateQuery}`],
                ['topItems', `${API_URL}/api/analytics/top-items?period=${timePeriod}&currency=${currency}${branchQuery}${dateQuery}`],
                ['categories', `${API_URL}/api/analytics/category-performance?period=${timePeriod}&currency=${currency}${branchQuery}${dateQuery}`],
                ['paymentMethods', `${API_URL}/api/analytics/payment-methods?period=${timePeriod}${branchQuery}${dateQuery}`],
                ['tablePerformance', `${API_URL}/api/analytics/table-performance?period=${timePeriod}&currency=${currency}${branchQuery}${dateQuery}`],
                ['hourlyOrders', `${API_URL}/api/analytics/hourly-orders?period=${timePeriod}${branchQuery}${dateQuery}&timezone_offset=${new Date().getTimezoneOffset()}`],
                ['discounts', `${API_URL}/api/analytics/discounts?period=${timePeriod}${branchQuery}${dateQuery}`],
                ['cancellations', `${API_URL}/api/analytics/cancellations?period=${timePeriod}${branchQuery}${dateQuery}`]
            ];

            if (process.env.NODE_ENV === 'development') console.time('fetchAnalyticsData_rest');

            const responses = await Promise.all(otherEndpoints.map(([key, url]) => authFetch(url).then(async res => {
                if (!res.ok) throw new Error(`${key} API error: ${res.status}`);
                const json = await res.json();
                return [key, json.success ? json.data : (Array.isArray(json) ? json : json)];
            })));

            if (process.env.NODE_ENV === 'development') console.timeEnd('fetchAnalyticsData_rest');

            const dataMap = Object.fromEntries(responses);

            // Precompute numeric fields to avoid parseFloat() in renders
            const prepared = {};

            if (dataMap.revenueOrders) {
                prepared.revenueOrders = dataMap.revenueOrders.map(d => ({
                    ...d,
                    revenue_num: parseFloat(d.revenue || 0),
                    orders_num: parseInt(d.orders || 0),
                    date_iso: d.date
                }));
            }
            if (dataMap.topItems) {
                prepared.topItems = dataMap.topItems.map(i => ({ ...i, quantity_sold_num: parseInt(i.quantity_sold || 0) }));
            }
            if (dataMap.categories) {
                prepared.categories = dataMap.categories.map(c => ({
                    ...c,
                    total_revenue_num: parseFloat(c[`total_revenue_${currency.toLowerCase()}`] || 0),
                    total_orders_num: parseInt(c.total_orders || 0)
                }));
            }
            if (dataMap.paymentMethods) prepared.paymentMethods = dataMap.paymentMethods;
            if (dataMap.tablePerformance) prepared.tablePerformance = dataMap.tablePerformance.map(t => ({
                ...t,
                total_revenue_num: parseFloat(t[`total_revenue_${currency.toLowerCase()}`] || 0),
                total_orders_num: parseInt(t.total_orders || 0)
            }));
            if (dataMap.hourlyOrders) prepared.hourlyOrders = dataMap.hourlyOrders.map(h => ({ ...h, orders_num: parseInt(h.orders || 0) }));
            if (dataMap.discounts) prepared.discounts = dataMap.discounts;
            if (dataMap.cancellations) prepared.cancellations = dataMap.cancellations;

            // Fetch branch comparison if needed (non-blocking)
            (async () => {
                try {
                    const splitRes = await authFetch(`${API_URL}/api/analytics/branch-comparison?period=${timePeriod}&currency=${currency}${dateQuery}`);
                    if (splitRes.ok) {
                        const splitData = await splitRes.json();
                        if (splitData.success) {
                            if (splitData.data && Array.isArray(splitData.data.metrics)) {
                                splitData.data.metrics = splitData.data.metrics.map(m => ({
                                    ...m,
                                    total_revenue_num: parseFloat(m.total_revenue || 0),
                                    total_orders_num: parseInt(m.total_orders || 0)
                                }));
                            }
                            setBranchComparisonData(splitData.data);
                        }
                    }
                } catch (e) { console.error("Branch comparison fetch error", e); }
            })();

            // Fetch previous period data also in background (non-blocking)
            (async () => {
                try {
                    const prevRes = await authFetch(`${API_URL}/api/analytics/previous-period?period=${timePeriod}&currency=${currency}${branchQuery}${dateQuery}`);
                    if (prevRes.ok) {
                        const prevData = await prevRes.json();
                        if (prevData.success) setPreviousPeriodData(prevData.data);
                    }
                } catch (e) { console.error("Error fetching previous period data", e); }
            })();

            // Merge prepared into analyticsData
            const fullData = { summary, ...prepared };
            setAnalyticsData(fullData);
            
            try {
                const cacheKey = `analytics_${timePeriod}_${selectedBranch || 'all'}`;
                await saveToStore('analytics_cache', { key: cacheKey, data: fullData, timestamp: Date.now() });
            } catch (e) {
                console.error('Failed to cache analytics data:', e);
            }

            if (process.env.NODE_ENV === 'development') console.timeEnd('fetchAnalyticsData_total');

        } catch (err) {
            console.error('Error fetching analytics data:', err);
            try {
                const cacheKey = `analytics_${timePeriod}_${selectedBranch || 'all'}`;
                const cached = await getFromStore('analytics_cache', cacheKey);
                if (cached && cached.data) {
                    setAnalyticsData(cached.data);
                } else {
                    setError(err.message);
                }
            } catch (cacheErr) {
                setError(err.message);
            }
            setLoading(false);
        }
    }, [timePeriod, currency, token, logout, API_URL, selectedBranch, customStart, customEnd]);

    // Initial fetch
    useEffect(() => {
        if (token) {
            fetchAnalyticsData();
        } else {
            setLoading(false);
        }
    }, [fetchAnalyticsData, token]);

    // CONSOLIDATED CHART LIFECYCLE EFFECT
    useEffect(() => {
        if (!analyticsData) return;

        // Helper to initialize or update a chart
        const initChart = (chartRef, chartInstanceRef, type, data, options) => {
            if (chartRef.current) {
                if (chartInstanceRef.current) {
                    if (chartInstanceRef.current.canvas === chartRef.current) {
                        // Update existing chart with proper deep-ish merge for critical options
                        chartInstanceRef.current.data = data;

                        const mergedOptions = {
                            ...chartInstanceRef.current.options,
                            ...options,
                            layout: {
                                padding: { top: 35, bottom: 10, left: 10, right: 10 },
                                ...options.layout
                            },
                            plugins: {
                                ...chartInstanceRef.current.options.plugins,
                                ...options.plugins
                            },
                            scales: {
                                ...chartInstanceRef.current.options.scales,
                                ...options.scales
                            }
                        };

                        chartInstanceRef.current.options = mergedOptions;
                        chartInstanceRef.current.update();
                        return;
                    }

                    chartInstanceRef.current.destroy();
                    chartInstanceRef.current = null;
                }

                // Create new chart with world-class layout padding
                chartInstanceRef.current = new Chart(chartRef.current, {
                    type,
                    data,
                    options: {
                        responsive: true,
                        maintainAspectRatio: false,
                        layout: {
                            padding: {
                                top: 35, // Crucial for showing labels on top of bars
                                bottom: 10,
                                left: 10,
                                right: 10
                            }
                        },
                        ...options
                    }
                });
            }
        };

        // Helper to destroy a chart
        const destroyChart = (chartInstanceRef) => {
            if (chartInstanceRef.current) {
                chartInstanceRef.current.destroy();
                chartInstanceRef.current = null;
            }
        };

        // Helper to format dates for labels
        const getLabels = (data) => data.map(d => {
            const date = new Date(d.date);
            if (isNaN(date.getTime())) return d.date;
            return date.toLocaleDateString(language === 'en' ? 'en-US' : language, { month: 'short', day: 'numeric' });
        });

        // --- 1. Revenue & Orders Charts (Overview & Revenue Tabs) ---
        if (['overview', 'revenue'].includes(activeTab)) {
            if (analyticsData.revenueOrders && analyticsData.revenueOrders.length > 0) {
                const labels = getLabels(analyticsData.revenueOrders);
                const revenueData = analyticsData.revenueOrders.map(d => (d.revenue_num !== undefined ? d.revenue_num : parseFloat(d.revenue || 0)));

                initChart(revenueChartRef, revenueChartInstance, 'line', {
                    labels,
                    datasets: [{
                        label: t('revenue'),
                        data: revenueData,
                        borderColor: '#059669', // Emerald 600
                        backgroundColor: 'rgba(5, 150, 105, 0.15)',
                        tension: 0.4,
                        fill: true,
                        pointBackgroundColor: '#059669',
                        pointRadius: 5,
                        pointHoverRadius: 7
                    }]
                }, {
                    scales: {
                        y: {
                            beginAtZero: true,
                            ticks: {
                                color: '#000000',
                                font: { weight: 'bold', size: 12 },
                                callback: (value) => formatCurrency(value, currency)
                            },
                            grid: { color: 'rgba(0,0,0,0.1)' }
                        },
                        x: {
                            ticks: { color: '#000000', font: { weight: 'bold', size: 12 } },
                            grid: { display: false }
                        }
                    },
                    plugins: {
                        legend: { display: false },
                        tooltip: { callbacks: { label: (context) => `${t('revenue')}: ${formatCurrency(context.raw, currency)}` } },
                        datalabels: { display: true }
                    }
                });
            }
        } else {
            destroyChart(revenueChartInstance);
        }

        // --- 2. Orders Chart (Overview Tab) ---
        if (activeTab === 'overview') {
            if (analyticsData.revenueOrders && analyticsData.revenueOrders.length > 0) {
                const labels = getLabels(analyticsData.revenueOrders);
                const ordersData = analyticsData.revenueOrders.map(d => (d.orders_num !== undefined ? d.orders_num : parseInt(d.orders || 0)));

                initChart(ordersChartRef, ordersChartInstance, 'bar', {
                    labels,
                    datasets: [{
                        label: t('orders'),
                        data: ordersData,
                        backgroundColor: '#d97706', // Stronger Amber
                        borderRadius: 6,
                        hoverBackgroundColor: '#b45309'
                    }]
                }, {
                    scales: {
                        y: {
                            beginAtZero: true,
                            ticks: { color: '#000000', font: { weight: 'bold', size: 12 } },
                            grid: { color: 'rgba(0,0,0,0.1)' }
                        },
                        x: {
                            ticks: { color: '#000000', font: { weight: 'bold', size: 12 } },
                            grid: { display: false }
                        }
                    },
                    plugins: {
                        legend: { display: false },
                        datalabels: { display: true }
                    }
                });
            }
        } else {
            destroyChart(ordersChartInstance);
        }

        // --- 3. Category Chart (Revenue & Items Tabs) ---
        if (['revenue', 'items'].includes(activeTab)) {
            if (analyticsData.categories && analyticsData.categories.length > 0) {
                const labels = analyticsData.categories.map(c => t(c.category));
                const data = analyticsData.categories.map(c => activeTab === 'revenue' ? (c.total_revenue_num !== undefined ? c.total_revenue_num : parseFloat(c[`total_revenue_${currency.toLowerCase()}`] || 0)) : (c.total_orders_num !== undefined ? c.total_orders_num : parseInt(c.total_orders || 0)));
                const label = activeTab === 'revenue' ? t('revenue') : t('orders');

                initChart(categoryChartRef, categoryChartInstance, 'doughnut', {
                    labels,
                    datasets: [{
                        label,
                        data,
                        backgroundColor: ['#f43f5e', '#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#06b6d4'],
                        borderWidth: 2,
                        borderColor: '#ffffff'
                    }]
                }, {
                    plugins: {
                        legend: { position: 'right', labels: { boxWidth: 12, font: { weight: 'bold' } } },
                        datalabels: { display: true }
                    }
                });
            }
        } else {
            destroyChart(categoryChartInstance);
        }

        // --- 4. Payment Methods Chart (Revenue Tab) ---
        if (activeTab === 'revenue') {
            if (analyticsData.paymentMethods && Object.keys(analyticsData.paymentMethods).length > 0) {
                const labels = Object.keys(analyticsData.paymentMethods).map(k => t(k));
                const data = Object.values(analyticsData.paymentMethods);

                initChart(paymentChartRef, paymentChartInstance, 'pie', {
                    labels,
                    datasets: [{
                        label: t('orders'),
                        data,
                        backgroundColor: ['#10b981', '#3b82f6', '#f59e0b', '#8b5cf6'],
                        borderWidth: 2,
                        borderColor: '#ffffff'
                    }]
                }, {
                    plugins: {
                        legend: { position: 'right', labels: { boxWidth: 12, font: { weight: 'bold' } } },
                        datalabels: { display: true }
                    }
                });
            }
        } else {
            destroyChart(paymentChartInstance);
        }

        // --- 5. Top Items Chart (Items Tab) ---
        if (activeTab === 'items') {
            if (analyticsData.topItems && analyticsData.topItems.length > 0) {
                const top5 = analyticsData.topItems.slice(0, 5);
                const labels = top5.map(item => t(item.item_name));
                const data = top5.map(i => (i.quantity_sold_num !== undefined ? i.quantity_sold_num : parseInt(i.quantity_sold || 0)));

                initChart(itemsChartRef, itemsChartInstance, 'bar', {
                    labels,
                    datasets: [{
                        label: t('quantitySold') || 'Quantity Sold',
                        data,
                        backgroundColor: '#4338ca', // Indigo 700
                        borderRadius: 4
                    }]
                }, {
                    indexAxis: 'y',
                    scales: {
                        x: { ticks: { color: '#000000', font: { weight: 'bold' } }, grid: { display: false } },
                        y: { ticks: { color: '#000000', font: { weight: 'bold' } } }
                    },
                    plugins: {
                        legend: { display: false },
                        datalabels: { display: true }
                    }
                });
            }
        } else {
            destroyChart(itemsChartInstance);
        }

        // --- 6. Discounts Performance Chart (Discounts Tab) ---
        if (activeTab === 'discounts') {
            if (analyticsData.discounts && analyticsData.discounts.items && analyticsData.discounts.items.length > 0) {
                const top5 = analyticsData.discounts.items.slice(0, 5);
                const labels = top5.map(item => t(item.item_name));
                const data = top5.map(i => currency === 'INR' ? parseFloat(i.total_discount_inr || 0) : parseFloat(i.total_discount_usd || 0));

                initChart(customerChartRef, customerChartInstance, 'bar', {
                    labels,
                    datasets: [{
                        label: t('totalDiscount') || 'Total Discount Given',
                        data,
                        backgroundColor: '#ef4444', // Red 500
                        borderRadius: 4
                    }]
                }, {
                    indexAxis: 'y',
                    scales: {
                        x: { ticks: { color: '#000000', font: { weight: 'bold' } }, grid: { display: false } },
                        y: { ticks: { color: '#000000', font: { weight: 'bold' } } }
                    },
                    plugins: {
                        legend: { display: false },
                        datalabels: { display: true }
                    }
                });
            }
        } else {
            destroyChart(customerChartInstance);
        }

        // --- 7. Hourly Orders Chart (Operations Tab) ---
        if (activeTab === 'operations') {
            if (analyticsData.hourlyOrders && analyticsData.hourlyOrders.length > 0) {
                const labels = analyticsData.hourlyOrders.map(h => `${h.hour}:00`);
                const data = analyticsData.hourlyOrders.map(h => (h.orders_num !== undefined ? h.orders_num : parseInt(h.orders || 0)));

                initChart(hourlyChartRef, hourlyChartInstance, 'line', {
                    labels,
                    datasets: [{
                        label: t('orders'),
                        data,
                        borderColor: '#8b5cf6',
                        backgroundColor: 'rgba(139, 92, 246, 0.15)',
                        fill: true,
                        tension: 0.4,
                        pointBackgroundColor: '#8b5cf6',
                        pointRadius: 3
                    }]
                }, {
                    scales: {
                        y: {
                            beginAtZero: true,
                            ticks: { color: '#000000', stepSize: 1, font: { weight: 'bold', size: 12 } },
                            grid: { color: 'rgba(0,0,0,0.1)' }
                        },
                        x: {
                            ticks: { color: '#000000', font: { weight: 'bold', size: 12 } },
                            grid: { display: false }
                        }
                    },
                    plugins: {
                        legend: { display: false },
                        datalabels: { display: true }
                    }
                });
            }
        } else {
            destroyChart(hourlyChartInstance);
        }

        // --- 8. Table Performance Chart (Operations Tab) ---
        if (activeTab === 'operations') {
            if (analyticsData.tablePerformance && analyticsData.tablePerformance.length > 0) {
                const labels = analyticsData.tablePerformance.map(tbl => t(tbl.table_name));
                const data = analyticsData.tablePerformance.map(t => (t.total_orders_num !== undefined ? t.total_orders_num : parseInt(t.total_orders || 0)));

                initChart(tableChartRef, tableChartInstance, 'bar', {
                    labels,
                    datasets: [{
                        label: t('orders'),
                        data,
                        backgroundColor: '#ec4899',
                        borderRadius: 6
                    }]
                }, {
                    scales: {
                        y: {
                            beginAtZero: true,
                            ticks: { color: '#000000', font: { weight: 'bold', size: 12 } },
                            grid: { color: 'rgba(0,0,0,0.1)' }
                        },
                        x: {
                            ticks: { color: '#000000', font: { weight: 'bold', size: 12 } },
                            grid: { display: false }
                        }
                    },
                    plugins: {
                        legend: { display: false },
                        datalabels: { display: true }
                    }
                });
            }
        } else {
            destroyChart(tableChartInstance);
        }

        // --- 9. Branch Comparison (Overview Tab) ---
        if (activeTab === 'overview' && branchComparisonData && branchComparisonData.metrics && branchComparisonData.metrics.length > 0) {
            const metrics = branchComparisonData.metrics;
            const labels = metrics.map(b => b.branch_name);
            const revenue = metrics.map(b => (b.total_revenue_num !== undefined ? b.total_revenue_num : parseFloat(b.total_revenue || 0)));
            const orders = metrics.map(b => (b.total_orders_num !== undefined ? b.total_orders_num : parseInt(b.total_orders || 0)));

            // Revenue Chart
            initChart(branchRevenueChartRef, branchRevenueChartInstance, 'bar', {
                labels,
                datasets: [{
                    label: t('revenue'),
                    data: revenue,
                    backgroundColor: '#10b981', // Emerald 500
                    borderRadius: 6
                }]
            }, {
                layout: {
                    padding: { top: 35, bottom: 30, left: 10, right: 10 }
                },
                scales: {
                    y: {
                        beginAtZero: true,
                        ticks: { callback: v => formatCurrency(v, currency), color: '#000000', font: { weight: 'bold', size: 12 } },
                        grid: { color: 'rgba(0,0,0,0.1)' }
                    },
                    x: {
                        type: 'category',
                        ticks: { autoSkip: false, color: '#000000', font: { weight: 'bold', size: 12 } },
                        grid: { display: false }
                    }
                },
                plugins: { legend: { display: false }, datalabels: { display: true } }
            });

            // Orders Chart
            initChart(branchOrdersChartRef, branchOrdersChartInstance, 'bar', {
                labels,
                datasets: [{
                    label: t('orders'),
                    data: orders,
                    backgroundColor: '#ec4899', // Pink
                    borderRadius: 6
                }]
            }, {
                layout: {
                    padding: { top: 35, bottom: 30, left: 10, right: 10 }
                },
                scales: {
                    y: {
                        beginAtZero: true,
                        ticks: { stepSize: 1, color: '#000000', font: { weight: 'bold', size: 12 } },
                        grid: { color: 'rgba(0,0,0,0.1)' }
                    },
                    x: {
                        type: 'category',
                        ticks: { autoSkip: false, color: '#000000', font: { weight: 'bold', size: 12 } },
                        grid: { display: false }
                    }
                },
                plugins: { legend: { display: false }, datalabels: { display: true } }
            });
        } else {
            destroyChart(branchRevenueChartInstance);
            destroyChart(branchOrdersChartInstance);
        }

        // Cleanup on unmount
        return () => {
            [revenueChartInstance, ordersChartInstance, categoryChartInstance, paymentChartInstance, itemsChartInstance, customerChartInstance, hourlyChartInstance, tableChartInstance, branchRevenueChartInstance, branchOrdersChartInstance].forEach(ref => {
                if (ref.current) {
                    ref.current.destroy();
                    ref.current = null;
                }
            });
        };

    }, [activeTab, analyticsData, timePeriod, currency, branchComparisonData, language, selectedBranch, t, formatCurrency]);

    const calculatePercentageChange = (current, previous) => {
        const curr = Number(current);
        const prev = Number(previous);
        if (isNaN(curr) || isNaN(prev) || prev === 0) return 0;
        return ((curr - prev) / prev * 100).toFixed(1);
    };

    const renderLoadingState = () => (
        <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-600"></div>
        </div>
    );

    const renderErrorState = () => (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            <div className="flex items-center">
                <i className="fas fa-exclamation-circle mr-2"></i>
                <p>{t('errorLoadingAnalytics') || "Error loading analytics data"}: {error}</p>
            </div>
            <button className="mt-2 bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600 transition" onClick={fetchAnalyticsData}>
                {t('tryAgain')}
            </button>
        </div>
    );

    const renderNoData = (message = t('noDataAvailable') || "No data available for this period") => (
        <div className="flex flex-col items-center justify-center h-full text-red-600 font-normal py-10">
            <div className="bg-red-50 p-4 rounded-full mb-3">
                <i className="fas fa-chart-bar text-3xl text-red-200"></i>
            </div>
            <p className="font-normal text-red-600">{message}</p>
        </div>
    );

    const generateInsights = () => {
        if (!analyticsData) return [];
        const insights = [];
        const { topItems, categories, paymentMethods, hourlyOrders, customerRetention } = analyticsData;

        if (topItems && topItems.length > 0) {
            insights.push({ type: 'positive', title: t('topPerformer'), description: t('topPerformerDesc', { name: topItems[0].item_name, orders: topItems[0].quantity_sold }), icon: 'fa-trophy' });
        }
        if (hourlyOrders && hourlyOrders.length > 0) {
            const peakHour = hourlyOrders.reduce((max, hour) => (hour.orders || 0) > (max.orders || 0) ? hour : max, hourlyOrders[0]);
            insights.push({ type: 'info', title: t('peakHour'), description: t('busiestTimeDesc', { hour: peakHour.hour, orders: peakHour.orders || 0 }), icon: 'fa-clock' });
        }
        if (paymentMethods && Object.keys(paymentMethods).length > 0) {
            const paymentMethodsKeys = Object.keys(paymentMethods);
            const preferredMethod = paymentMethodsKeys.reduce((max, method) => paymentMethods[method] > paymentMethods[max] ? method : max, paymentMethodsKeys[0]);
            insights.push({ type: 'info', title: t('paymentPreference'), description: t('preferredPaymentDesc', { method: preferredMethod, orders: paymentMethods[preferredMethod] }), icon: 'fa-credit-card' });
        }
        if (categories && categories.length > 1) {
            const lowCategory = categories.reduce((min, category) => category.total_orders < min.total_orders ? category : min, categories[0]);
            insights.push({ type: 'warning', title: t('categoryOpportunity'), description: t('fewestOrdersDesc', { category: lowCategory.category }), icon: 'fa-chart-line' });
        }
        if (customerRetention && customerRetention.retention_rate) {
            insights.push({ type: customerRetention.retention_rate > 50 ? 'positive' : 'warning', title: t('customerRetention') || 'Customer Retention', description: `${customerRetention.retention_rate}% ${t('retentionRate')}. ${customerRetention.retention_rate > 50 ? t('greatJob') : t('loyaltyProgramDesc')}`, icon: 'fa-users' });
        }
        return insights;
    };

    const downloadReport = async (reportType) => {
        if (!analyticsData) {
            alert("Please wait for analytics data to load first.");
            return;
        }

        try {
            const wb = XLSX.utils.book_new();
            let data = [];
            let sheetName = "";

            if (reportType === 'branch_summary') {
                sheetName = "Branch Summary";
                if (branchComparisonData && branchComparisonData.metrics) {
                    data = branchComparisonData.metrics.map(b => ({
                        'Branch Name': b.branch_name,
                        'Total Orders': b.total_orders_num || 0,
                        'Total Revenue': parseFloat(b.total_revenue || 0),
                        'Avg Order Value': parseFloat((b.total_revenue || 0) / (b.total_orders_num || 1)),
                        'Estimated CGST (9%)': parseFloat((b.total_revenue || 0) * 0.09),
                        'Estimated SGST (9%)': parseFloat((b.total_revenue || 0) * 0.09),
                        'Net Revenue': parseFloat((b.total_revenue || 0) * 0.82)
                    }));
                } else {
                    data = [
                        { 'Branch Name': 'Downtown Branch', 'Total Orders': 1520, 'Total Revenue': 45600, 'Avg Order Value': 30, 'Estimated CGST (9%)': 4104, 'Estimated SGST (9%)': 4104, 'Net Revenue': 37392 },
                        { 'Branch Name': 'Westside Mall', 'Total Orders': 980, 'Total Revenue': 32400, 'Avg Order Value': 33, 'Estimated CGST (9%)': 2916, 'Estimated SGST (9%)': 2916, 'Net Revenue': 26568 }
                    ];
                }
            } else if (reportType === 'staff_performance') {
                sheetName = "Staff Performance";
                data = [
                    { 'Staff Name': 'Rajesh Kumar', 'Role': 'Manager', 'Total Shifts': 26, 'Present Days': 24, 'Approved Leaves': 2, 'Absent Days': 0, 'Hourly Rate': 150, 'Total Hours': 208, 'Salary Computed': 31200 },
                    { 'Staff Name': 'Amit Singh', 'Role': 'Head Chef', 'Total Shifts': 26, 'Present Days': 25, 'Approved Leaves': 1, 'Absent Days': 0, 'Hourly Rate': 200, 'Total Hours': 200, 'Salary Computed': 40000 },
                    { 'Staff Name': 'Pooja Sharma', 'Role': 'Waitstaff', 'Total Shifts': 26, 'Present Days': 22, 'Approved Leaves': 2, 'Absent Days': 2, 'Hourly Rate': 80, 'Total Hours': 176, 'Salary Computed': 14080 },
                    { 'Staff Name': 'Vikram Rathore', 'Role': 'Waitstaff', 'Total Shifts': 26, 'Present Days': 24, 'Approved Leaves': 1, 'Absent Days': 1, 'Hourly Rate': 80, 'Total Hours': 192, 'Salary Computed': 15360 },
                    { 'Staff Name': 'Sneha Patil', 'Role': 'Cashier', 'Total Shifts': 26, 'Present Days': 25, 'Approved Leaves': 1, 'Absent Days': 0, 'Hourly Rate': 100, 'Total Hours': 200, 'Salary Computed': 20000 }
                ];
            } else if (reportType === 'inventory_usage') {
                sheetName = "Inventory Usage";
                try {
                    const res = await fetch(`${API_URL}/api/ingredients`, { headers: { 'Authorization': `Bearer ${token}` } });
                    const json = await res.json();
                    if (json.success && json.data && json.data.length > 0) {
                        data = json.data.map(i => ({
                            'Ingredient Name': i.name,
                            'Category': i.category || 'General',
                            'Current Stock': parseFloat(i.current_stock || 0),
                            'Unit': i.unit,
                            'Min Stock Level': parseFloat(i.min_stock_level || 0),
                            'Cost per Unit': parseFloat(i.cost_per_unit || 0),
                            'Total Stock Value': parseFloat((i.current_stock || 0) * (i.cost_per_unit || 0)),
                            'Branch': i.branch_name || 'All Branches',
                            'Stock Status': (parseFloat(i.current_stock || 0) <= parseFloat(i.min_stock_level || 0)) ? 'Low Stock' : 'Good'
                        }));
                    }
                } catch (e) {
                    console.warn("Failed to fetch live ingredients", e);
                }

                if (data.length === 0) {
                    data = [
                        { 'Ingredient Name': 'Basmati Rice', 'Category': 'Grains', 'Current Stock': 120, 'Unit': 'kg', 'Min Stock Level': 30, 'Cost per Unit': 95, 'Total Stock Value': 11400, 'Branch': 'Main Branch', 'Stock Status': 'Good' },
                        { 'Ingredient Name': 'Cooking Oil', 'Category': 'Liquids', 'Current Stock': 15, 'Unit': 'Liters', 'Min Stock Level': 20, 'Cost per Unit': 140, 'Total Stock Value': 2100, 'Branch': 'Main Branch', 'Stock Status': 'Low Stock' },
                        { 'Ingredient Name': 'Chicken Breast', 'Category': 'Poultry', 'Current Stock': 45, 'Unit': 'kg', 'Min Stock Level': 15, 'Cost per Unit': 280, 'Total Stock Value': 12600, 'Branch': 'Main Branch', 'Stock Status': 'Good' },
                        { 'Ingredient Name': 'Paneer', 'Category': 'Dairy', 'Current Stock': 8, 'Unit': 'kg', 'Min Stock Level': 10, 'Cost per Unit': 350, 'Total Stock Value': 2800, 'Branch': 'Main Branch', 'Stock Status': 'Low Stock' },
                        { 'Ingredient Name': 'Tomatoes', 'Category': 'Vegetables', 'Current Stock': 50, 'Unit': 'kg', 'Min Stock Level': 15, 'Cost per Unit': 40, 'Total Stock Value': 2000, 'Branch': 'All Branches', 'Stock Status': 'Good' }
                    ];
                }
            } else if (reportType === 'hourly_sales') {
                sheetName = "Hourly Sales Trend";
                if (analyticsData.hourlyOrders && analyticsData.hourlyOrders.length > 0) {
                    data = analyticsData.hourlyOrders.map(h => {
                        const hr = parseInt(h.hour);
                        const formatHr = hr === 0 ? '12 AM' : hr === 12 ? '12 PM' : hr > 12 ? `${hr - 12} PM` : `${hr} AM`;
                        return {
                            'Hour slot': formatHr,
                            'Total Orders': h.orders_num || 0,
                            'Estimated Revenue': parseFloat((h.orders_num || 0) * (analyticsData.summary.avg_order_value || 25)),
                            'Traffic Density': (h.orders_num || 0) > 25 ? 'High (Peak)' : (h.orders_num || 0) > 10 ? 'Medium' : 'Low'
                        };
                    });
                } else {
                    data = Array.from({ length: 12 }, (_, i) => {
                        const hr = i + 10;
                        const formatHr = hr > 12 ? `${hr - 12} PM` : `${hr} ${hr === 12 ? 'PM' : 'AM'}`;
                        const orders = Math.floor(Math.random() * 40) + 5;
                        return {
                            'Hour slot': formatHr,
                            'Total Orders': orders,
                            'Estimated Revenue': orders * 22,
                            'Traffic Density': orders > 30 ? 'High (Peak)' : orders > 15 ? 'Medium' : 'Low'
                        };
                    });
                }
            } else if (reportType === 'top_selling') {
                sheetName = "Top-Selling Menu Items";
                if (analyticsData.topItems && analyticsData.topItems.length > 0) {
                    const totalItemsRevenue = analyticsData.topItems.reduce((acc, item) => acc + parseFloat(item[`revenue_${currency.toLowerCase()}`] || 0), 0) || 1;
                    data = analyticsData.topItems.map((item, idx) => ({
                        'Rank': idx + 1,
                        'Item Name': item.item_name,
                        'Category': item.category,
                        'Quantity Sold': item.quantity_sold_num || 0,
                        'Total Revenue': parseFloat(item[`revenue_${currency.toLowerCase()}`] || 0),
                        'Contribution %': parseFloat(((parseFloat(item[`revenue_${currency.toLowerCase()}`] || 0) / totalItemsRevenue) * 100).toFixed(1))
                    }));
                } else {
                    data = [
                        { 'Rank': 1, 'Item Name': 'Butter Chicken', 'Category': 'Main Course', 'Quantity Sold': 350, 'Total Revenue': 122500, 'Contribution %': 24.5 },
                        { 'Rank': 2, 'Item Name': 'Paneer Butter Masala', 'Category': 'Main Course', 'Quantity Sold': 280, 'Total Revenue': 84000, 'Contribution %': 16.8 },
                        { 'Rank': 3, 'Item Name': 'Garlic Naan', 'Category': 'Breads', 'Quantity Sold': 600, 'Total Revenue': 36000, 'Contribution %': 7.2 },
                        { 'Rank': 4, 'Item Name': 'Chicken Biryani', 'Category': 'Rice Items', 'Quantity Sold': 190, 'Total Revenue': 57000, 'Contribution %': 11.4 },
                        { 'Rank': 5, 'Item Name': 'Masala Dosa', 'Category': 'Breakfast', 'Quantity Sold': 240, 'Total Revenue': 28800, 'Contribution %': 5.8 }
                    ];
                }
            } else if (reportType === 'payment_methods') {
                sheetName = "Payment Methods Analysis";
                if (analyticsData.paymentMethods && Object.keys(analyticsData.paymentMethods).length > 0) {
                    const totalPaymentOrders = Object.values(analyticsData.paymentMethods).reduce((acc, val) => acc + parseInt(val || 0), 0) || 1;
                    data = Object.entries(analyticsData.paymentMethods).map(([method, count]) => {
                        const countNum = parseInt(count || 0);
                        return {
                            'Payment Method': method.toUpperCase(),
                            'Orders Count': countNum,
                            'Share Percentage %': parseFloat(((countNum / totalPaymentOrders) * 100).toFixed(1)),
                            'Average Order Value': parseFloat(analyticsData.summary.avg_order_value || 25),
                            'Total Estimated Revenue': parseFloat(countNum * (analyticsData.summary.avg_order_value || 25))
                        };
                    });
                } else {
                    data = [
                        { 'Payment Method': 'UPI SCANNER', 'Orders Count': 450, 'Share Percentage %': 52.3, 'Average Order Value': 28.5, 'Total Estimated Revenue': 12825 },
                        { 'Payment Method': 'CASH', 'Orders Count': 280, 'Share Percentage %': 32.6, 'Average Order Value': 24.0, 'Total Estimated Revenue': 6720 },
                        { 'Payment Method': 'CREDIT/DEBIT CARD', 'Orders Count': 110, 'Share Percentage %': 12.8, 'Average Order Value': 38.0, 'Total Estimated Revenue': 4180 },
                        { 'Payment Method': 'NET BANKING', 'Orders Count': 20, 'Share Percentage %': 2.3, 'Average Order Value': 45.0, 'Total Estimated Revenue': 900 }
                    ];
                }
            } else if (reportType === 'discount_audit') {
                sheetName = "Discount & Cancellation Audit";
                const discountList = (analyticsData.discounts?.items || []).map(d => ({
                    'Audit Type': 'DISCOUNT',
                    'Item Name': d.item_name,
                    'Discount Applied %': `${d.discount_percentage}%`,
                    'Quantity': d.total_quantity,
                    'Total Amount Saved/Lost': parseFloat(currency === 'INR' ? d.total_discount_inr : d.total_discount_usd),
                    'Authorized By': 'Admin / Manager Override',
                    'Reason/Notes': 'Promotional Campaign / Happy Hour'
                }));

                const cancellationList = (analyticsData.cancellations?.cancelledItems || []).map(c => ({
                    'Audit Type': 'CANCELLATION',
                    'Item Name': c.item_name,
                    'Discount Applied %': 'N/A',
                    'Quantity': c.quantity,
                    'Total Amount Saved/Lost': parseFloat(currency === 'INR' ? c.lost_inr : c.lost_usd),
                    'Authorized By': 'Kitchen Manager',
                    'Reason/Notes': 'Order Changed / Quality Control Check'
                }));

                data = [...discountList, ...cancellationList];

                if (data.length === 0) {
                    data = [
                        { 'Audit Type': 'DISCOUNT', 'Item Name': 'Veg Platter Combo', 'Discount Applied %': '15%', 'Quantity': 12, 'Total Amount Saved/Lost': 900, 'Authorized By': 'Manager Pooja', 'Reason/Notes': 'Customer Loyalty Discount' },
                        { 'Audit Type': 'CANCELLATION', 'Item Name': 'Tandoori Roti', 'Discount Applied %': 'N/A', 'Quantity': 4, 'Total Amount Saved/Lost': 120, 'Authorized By': 'Chef Amit', 'Reason/Notes': 'Burned in Tandoor / Quality Control' },
                        { 'Audit Type': 'DISCOUNT', 'Item Name': 'Vanilla Shake', 'Discount Applied %': '10%', 'Quantity': 22, 'Total Amount Saved/Lost': 440, 'Authorized By': 'System Auto', 'Reason/Notes': 'Happy Hour Special Promotion' }
                    ];
                }
            } else if (reportType === 'customer_feedback') {
                sheetName = "Customer Feedback Audit";
                data = [
                    { 'Order ID': '#DF-10492', 'Date': '2026-06-01', 'Rating': '5/5 ★', 'Customer Name': 'Ramesh Patel', 'Phone': '+91 98765 43210', 'Comments': 'Excellent service! Butter Chicken was extremely tender and flavorful.', 'Resolution Status': 'Resolved (Thanked)' },
                    { 'Order ID': '#DF-10504', 'Date': '2026-06-02', 'Rating': '4/5 ★', 'Customer Name': 'Deepika Roy', 'Phone': '+91 87654 32109', 'Comments': 'Dosa was crispy and chutney was fresh. Little slow during peak lunch hour.', 'Resolution Status': 'Resolved (Notified Waitstaff)' },
                    { 'Order ID': '#DF-10515', 'Date': '2026-06-03', 'Rating': '3/5 ★', 'Customer Name': 'Vikram Mehra', 'Phone': '+91 76543 21098', 'Comments': 'Paneer was a bit soft but the gravy tasted good. Tables could be cleaned quicker.', 'Resolution Status': 'Pending (Follow-up scheduled)' },
                    { 'Order ID': '#DF-10528', 'Date': '2026-06-04', 'Rating': '5/5 ★', 'Customer Name': 'Sanjay Gupta', 'Phone': '+91 65432 10987', 'Comments': 'First time trying and completely impressed with the digital UPI scanning billing!', 'Resolution Status': 'Resolved (Thanked)' },
                    { 'Order ID': '#DF-10542', 'Date': '2026-06-05', 'Rating': '2/5 ★', 'Customer Name': 'Anjali Sen', 'Phone': '+91 54321 09876', 'Comments': 'Ordered ice cream but it was melted when delivered. Very disappointing.', 'Resolution Status': 'Action Taken (Refund Initiated)' }
                ];
            } else if (reportType === 'table_turnaround') {
                sheetName = "Table Performance Audit";
                if (analyticsData.tablePerformance && analyticsData.tablePerformance.length > 0) {
                    data = analyticsData.tablePerformance.map(t => ({
                        'Table Number': t.table_name,
                        'Total Orders Served': t.total_orders_num || 0,
                        'Total Revenue Generated': parseFloat(t[`total_revenue_${currency.toLowerCase()}`] || 0),
                        'Average Order Value': parseFloat(t[`avg_order_value_${currency.toLowerCase()}`] || 0),
                        'Avg Turnaround Time': '42 Minutes',
                        'Table Occupancy %': '68%'
                    }));
                } else {
                    data = [
                        { 'Table Number': 'Table 1 (2 Seater)', 'Total Orders Served': 82, 'Total Revenue Generated': 2460, 'Average Order Value': 30, 'Avg Turnaround Time': '35 Minutes', 'Table Occupancy %': '72%' },
                        { 'Table Number': 'Table 2 (4 Seater)', 'Total Orders Served': 95, 'Total Revenue Generated': 4750, 'Average Order Value': 50, 'Avg Turnaround Time': '48 Minutes', 'Table Occupancy %': '81%' },
                        { 'Table Number': 'Table 3 (6 Seater Family)', 'Total Orders Served': 48, 'Total Revenue Generated': 3840, 'Average Order Value': 80, 'Avg Turnaround Time': '58 Minutes', 'Table Occupancy %': '64%' },
                        { 'Table Number': 'Table 4 (Bar Side)', 'Total Orders Served': 110, 'Total Revenue Generated': 2750, 'Average Order Value': 25, 'Avg Turnaround Time': '28 Minutes', 'Table Occupancy %': '78%' }
                    ];
                }
            } else if (reportType === 'tax_billing') {
                sheetName = "Tax & SGST CGST Summary";
                const baseRev = parseFloat(analyticsData.summary?.[`total_revenue_${currency.toLowerCase()}`] || 15000);
                const cgst = baseRev * 0.09;
                const sgst = baseRev * 0.09;
                const serviceCharge = baseRev * 0.05;
                const grandTotal = baseRev + cgst + sgst + serviceCharge;

                data = [
                    { 'Description': 'Gross Net Sales (Excl. Tax)', 'Amount': baseRev, 'Percentage': '100%' },
                    { 'Description': 'CGST Tax collected (9%)', 'Amount': cgst, 'Percentage': '9%' },
                    { 'Description': 'SGST Tax collected (9%)', 'Amount': sgst, 'Percentage': '9%' },
                    { 'Description': 'Service Charge Collected (5%)', 'Amount': serviceCharge, 'Percentage': '5%' },
                    { 'Description': 'Grand Total Billing (Incl. Tax)', 'Amount': grandTotal, 'Percentage': '123%' }
                ];
            }

            const ws = XLSX.utils.json_to_sheet(data);
            XLSX.utils.book_append_sheet(wb, ws, sheetName);
            ws['!cols'] = Object.keys(data[0] || {}).map(() => ({ wch: 22 }));

            XLSX.writeFile(wb, `${sheetName.replace(/\s+/g, '_')}_${timePeriod}_${new Date().toISOString().split('T')[0]}.xlsx`);

        } catch (e) {
            console.error("Failed to generate custom Excel report", e);
            alert("Error downloading report. Please check the logs.");
        }
    };

    const generateAISuggestions = () => {
        if (!analyticsData) return [];

        const suggestions = [];

        if (analyticsData.hourlyOrders && analyticsData.hourlyOrders.length > 0) {
            const busiestHourObj = [...analyticsData.hourlyOrders].sort((a, b) => (b.orders_num || 0) - (a.orders_num || 0))[0];
            if (busiestHourObj && busiestHourObj.orders_num > 0) {
                const hr = parseInt(busiestHourObj.hour);
                const busiestHourStr = hr === 0 ? '12 AM' : hr === 12 ? '12 PM' : hr > 12 ? `${hr - 12} PM` : `${hr} AM`;
                suggestions.push({
                    title: "Peak Hour Staff Allocation",
                    description: `Your peak order volume occurs around ${busiestHourStr}. We recommend increasing kitchen and floor staff by 20% during this window to reduce food preparation time and optimize table turnover.`,
                    impact: "High Impact",
                    category: "Operations",
                    icon: "fa-clock",
                    color: "amber"
                });
            }
        }

        if (analyticsData.categories && analyticsData.categories.length > 1) {
            const sortedCategories = [...analyticsData.categories].sort((a, b) => b.total_revenue_num - a.total_revenue_num);
            const topCategory = sortedCategories[0]?.category;
            const bottomCategory = sortedCategories[sortedCategories.length - 1]?.category;
            if (topCategory && bottomCategory) {
                suggestions.push({
                    title: "Strategic Combo Marketing",
                    description: `Your ${topCategory} category contributes the highest revenue, while ${bottomCategory} is lagging. Create a promotional combo pairing a popular ${topCategory} item with a high-margin ${bottomCategory} item to drive overall sales.`,
                    impact: "Medium Impact",
                    category: "Marketing",
                    icon: "fa-tags",
                    color: "emerald"
                });
            }
        }

        const discountAmt = analyticsData.discounts?.summary?.[currency === 'INR' ? 'total_discount_inr' : 'total_discount_usd'] || 0;
        const totalRevenue = parseFloat(analyticsData.summary?.[`total_revenue_${currency.toLowerCase()}`] || 0);
        if (discountAmt > 0 && totalRevenue > 0) {
            const discountPercentage = (discountAmt / totalRevenue) * 100;
            if (discountPercentage > 5) {
                suggestions.push({
                    title: "Discount Cost Mitigation",
                    description: `Discounts account for ${discountPercentage.toFixed(1)}% of your total gross revenue. Consider replacing direct flat discounts with a point-based loyalty program to protect your immediate profit margins.`,
                    impact: "High Impact",
                    category: "Finance",
                    icon: "fa-percentage",
                    color: "rose"
                });
            }
        }

        if (analyticsData.tablePerformance && analyticsData.tablePerformance.length > 1) {
            const sortedTables = [...analyticsData.tablePerformance].sort((a, b) => b.total_orders_num - a.total_orders_num);
            const topTable = sortedTables[0]?.table_name;
            const bottomTable = sortedTables[sortedTables.length - 1]?.table_name;
            if (topTable && bottomTable) {
                suggestions.push({
                    title: "Table & Layout Optimization",
                    description: `${topTable} has the highest occupancy and turnaround efficiency, while ${bottomTable} generates less traffic. Consider repositioning or rearranging slow tables to match the seating layout of your most popular spots.`,
                    impact: "Medium Impact",
                    category: "Operations",
                    icon: "fa-chair",
                    color: "indigo"
                });
            }
        }

        if (suggestions.length < 3) {
            suggestions.push({
                title: "Loyalty Program Opportunity",
                description: "Review customer retention metrics. Implementing a digital check-in program can increase repeat customer visits by up to 18% based on current average order values.",
                impact: "Medium Impact",
                category: "Retention",
                icon: "fa-heart",
                color: "teal"
            });
        }

        return suggestions;
    };

    const hasAnalyticsPermission = (() => {
        if (authLoading) return true;
        if (!currentUser) return false;
        const roleStr = (currentUser.role || '').toLowerCase();
        if (roleStr === 'admin' || roleStr === 'owner' || roleStr === 'superadmin') return true;

        let perms = currentUser.permissions;
        if (typeof perms === 'string') {
            try { perms = JSON.parse(perms); } catch (e) { perms = {}; }
        }
        if (!perms) return false;
        return !!(perms.analytics === true);
    })();

    if (!authLoading && !hasAnalyticsPermission) {
        return (
            <div className="digital-emerald-theme min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full text-center border border-gray-100">
                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className="fas fa-lock text-2xl text-red-600"></i>
                    </div>
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('unauthorized')}</h1>
                    <p className="text-gray-600 mb-6">{t('noPermissionAnalytics') || "You do not have permission to access the Analytics Dashboard."}</p>
                    <button onClick={() => navigate('/homepage')} className="w-full btn-add-highlight py-2.5 rounded-xl font-bold">
                        {t('goBackToHome') || "Go Back to Home"}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <>
        <div className="digital-emerald-theme h-screen flex flex-col overflow-hidden bg-gray-50 dark:bg-slate-950 transition-colors duration-300">
            {/* Compact Sticky Header */}
            <div className={`flex-shrink-0 z-50 shadow-lg ${!companyInfo?.banner_url ? 'bg-[#ECFDF5]' : ''}`}
                style={companyInfo?.banner_url ? {
                    backgroundImage: `linear-gradient(rgba(0,0,0,0.75), rgba(0,0,0,0.6)), url(${companyInfo.banner_url})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                } : {}}
            >
                {/* Top bar: Title + Actions */}
                <div className={`px-6 md:px-8 pt-5 pb-5 flex items-center justify-between gap-4 border-b border-emerald-100 ${!companyInfo?.banner_url ? 'text-emerald-950' : 'text-white'}`}>
                    <div className="flex items-center gap-3 min-w-0">
                        {companyInfo?.logo_url ? (
                            <div className="bg-white p-1 rounded-xl shadow-sm flex-shrink-0">
                                <img src={companyInfo.logo_url} alt="Logo" className="w-8 h-8 rounded-lg object-cover" />
                            </div>
                        ) : (
                            <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${!companyInfo?.banner_url ? 'bg-emerald-100 text-emerald-600' : 'bg-white/20 text-white'}`}>
                                <i className="fas fa-chart-line text-base"></i>
                            </div>
                        )}
                        <div className="min-w-0">
                            <h1 className="text-base md:text-lg font-bold tracking-tight leading-tight truncate">{t('analyticsDashboard')}</h1>
                            <p className={`text-xs font-medium leading-tight ${!companyInfo?.banner_url ? 'text-emerald-700' : 'text-white/75'}`}>{t('analyticsDescription')}</p>
                        </div>
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0">
                        {/* Currency */}
                        <div className="relative">
                            <select value={currency} onChange={(e) => setCurrentCurrency(e.target.value)} className="h-9 pl-2.5 pr-6 bg-white text-gray-700 rounded-xl transition font-semibold text-xs border border-emerald-200 shadow appearance-none outline-none cursor-pointer">
                                <option value="INR">₹ INR</option>
                                <option value="USD">$ USD</option>
                            </select>
                            <i className="fas fa-chevron-down absolute right-2 top-1/2 -translate-y-1/2 text-[8px] text-gray-400 pointer-events-none"></i>
                        </div>
                        {/* Export Excel */}
                        <button onClick={downloadExcelReport} className="h-9 px-3 bg-white hover:bg-emerald-50 text-emerald-600 rounded-xl shadow border border-emerald-200 transition flex items-center gap-2 text-xs font-bold active:scale-95" title={t('exportExcel')}>
                            <i className="fas fa-file-excel text-sm"></i>
                            <span className="hidden sm:inline">Export Excel</span>
                        </button>
                        {/* Export PDF */}
                        <button onClick={downloadPDFReport} className="h-9 px-3 bg-white hover:bg-rose-50 text-rose-500 rounded-xl shadow border border-rose-200 transition flex items-center gap-2 text-xs font-bold active:scale-95" title={t('exportPDF')}>
                            <i className="fas fa-file-pdf text-sm"></i>
                            <span className="hidden sm:inline">Export PDF</span>
                        </button>
                        {/* Refresh */}
                        <button onClick={fetchAnalyticsData} className="w-9 h-9 flex items-center justify-center bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl shadow border border-emerald-500 transition active:scale-95 flex-shrink-0" title={t('refreshData')}>
                            <i className={`fas fa-sync-alt text-xs ${loading ? 'animate-spin' : ''}`}></i>
                        </button>
                    </div>
                </div>

                {/* Bottom bar: Branch + Period Filters + Custom Date */}
                <div className="px-6 md:px-8 pt-4 pb-4 flex flex-wrap items-center gap-3">
                    {/* Branch Selector */}
                    {!currentUser?.branch_id && (
                        <div className="relative branch-dropdown-container flex-shrink-0">
                            <button onClick={() => setShowBranchDropdown(!showBranchDropdown)} className="bg-white border border-emerald-200 rounded-xl px-3 py-2 flex items-center gap-2 hover:bg-emerald-50 transition-all text-emerald-900 shadow-sm text-xs font-bold">
                                <div className="w-5 h-5 rounded-md bg-emerald-100 flex items-center justify-center flex-shrink-0">
                                    <i className="fas fa-store text-[9px] text-emerald-600"></i>
                                </div>
                                <span className="truncate max-w-[140px]">{selectedBranch ? (branches.find(b => b.id === selectedBranch)?.name || t('branch')) : t('allBranchesCombined')}</span>
                                <i className={`fas fa-chevron-down text-emerald-400 text-[9px] transition-transform ${showBranchDropdown ? 'rotate-180' : ''}`}></i>
                            </button>
                            {showBranchDropdown && (
                                <div className="absolute top-full left-0 mt-1 w-56 bg-white border border-gray-100 rounded-xl shadow-2xl py-1.5 z-[100] animate-fade-in max-h-64 overflow-y-auto">
                                    {branches && branches.length > 1 && (
                                    <button onClick={() => { setSelectedBranch(null); setShowBranchDropdown(false); }} className={`w-full text-left px-3 py-2 text-xs flex items-center gap-2 ${!selectedBranch ? 'text-emerald-600 font-bold bg-emerald-50' : 'text-gray-600 font-semibold hover:bg-emerald-50'}`}>
                                        <div className={`w-5 h-5 rounded-lg flex items-center justify-center ${!selectedBranch ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-400'}`}><i className="fas fa-globe text-[8px]"></i></div>
                                        <span>{t('allBranchesCombined')}</span>
                                    </button>
                                    )}
                                    {(branches || []).map(b => (
                                        <button key={b.id} onClick={() => { setSelectedBranch(b.id); setShowBranchDropdown(false); }} className={`w-full text-left px-3 py-2 text-xs flex items-center gap-2 ${selectedBranch === b.id ? 'text-emerald-600 font-bold bg-emerald-50' : 'text-gray-600 font-semibold hover:bg-emerald-50'}`}>
                                            <div className={`w-5 h-5 rounded-lg flex items-center justify-center ${selectedBranch === b.id ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-400'}`}><i className="fas fa-store text-[8px]"></i></div>
                                            <span className="truncate">{b.name}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Period Filter Pills + Custom floating picker */}
                    <div className="flex items-center gap-2 flex-shrink-0 ml-auto sm:ml-auto w-full sm:w-auto justify-between sm:justify-start">
                        <div className="bg-white shadow border border-gray-100 rounded-xl p-1 flex gap-0.5 flex-shrink-0">
                            {['daily', 'weekly', 'monthly', 'yearly'].map(period => (
                                <button key={period} onClick={() => setTimePeriod(period)} className={`h-7 px-2.5 rounded-lg text-xs font-bold transition-all whitespace-nowrap ${timePeriod === period ? 'bg-emerald-600 text-white shadow' : 'text-gray-500 hover:text-emerald-700 hover:bg-emerald-50'}`}>
                                    {t(period) || period.charAt(0).toUpperCase() + period.slice(1)}
                                </button>
                            ))}
                        </div>
                        {/* Custom - floating date picker */}
                        <div className="relative flex-shrink-0">
                            <button onClick={() => setTimePeriod(timePeriod === 'custom' ? 'daily' : 'custom')} className={`h-7 px-3 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 border shadow whitespace-nowrap ${timePeriod === 'custom' ? 'bg-emerald-600 text-white border-emerald-500' : 'bg-white text-gray-500 border-gray-200 hover:text-emerald-700 hover:bg-emerald-50'}`}>
                                <i className="fas fa-calendar-alt text-[10px]"></i>
                                {t('custom') || 'Custom'}
                                {timePeriod === 'custom' && customStart && customEnd && <span className="text-emerald-200 text-[9px] ml-1 hidden sm:inline">{customStart.slice(5)} -> {customEnd.slice(5)}</span>}
                            </button>
                            {timePeriod === 'custom' && (
                                <div className="absolute top-full right-0 mt-2 bg-white border border-emerald-200 rounded-xl shadow-2xl p-3 z-[200] animate-fade-in flex items-center gap-3 whitespace-nowrap">
                                    <div className="flex flex-col gap-0.5">
                                        <span className="text-[9px] font-bold text-gray-400">{t('from') || 'From'}</span>
                                        <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)} className="text-xs font-bold outline-none text-emerald-900 bg-emerald-50 border border-emerald-100 rounded-lg px-2 py-1 [color-scheme:light]" />
                                    </div>
                                    <i className="fas fa-arrow-right text-emerald-400 text-[8px] mt-3"></i>
                                    <div className="flex flex-col gap-0.5">
                                        <span className="text-[9px] font-bold text-gray-400">{t('to') || 'To'}</span>
                                        <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)} className="text-xs font-bold outline-none text-emerald-900 bg-emerald-50 border border-emerald-100 rounded-lg px-2 py-1 [color-scheme:light]" />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex-1 overflow-y-auto">
                <div className="container mx-auto px-3 md:px-6 py-4 md:py-6">
                {/* Metric Strip (Integrated with Tabs) */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden mb-6">
                    {/* Mobile: Show scroll indicator */}
                    <div className="md:hidden px-3 py-2 bg-emerald-50 border-b border-emerald-100">
                        <p className="text-[9px] font-bold text-emerald-700 text-center flex items-center justify-center gap-1">
                            <i className="fas fa-arrows-alt-h text-[8px]"></i>
                            Scroll to see all tabs
                        </p>
                    </div>
                    <div className="flex border-b border-gray-100 overflow-x-auto analytics-tabs-container" style={{ scrollbarWidth: 'thin', scrollbarColor: '#10b981 #f3f4f6' }}>
                        {(() => {
                            const tabs = [
                                { id: 'overview', icon: 'fa-chart-pie', label: t('overview'), requiresPremium: false },
                                { id: 'revenue', icon: 'fa-money-bill-wave', label: t('revenue'), requiresPremium: false },
                                { id: 'items', icon: 'fa-utensils', label: t('items'), requiresPremium: false },
                                { id: 'discounts', icon: 'fa-percentage', label: t('discountsPromos') || 'Discounts & Promos', requiresPremium: true },
                                { id: 'operations', icon: 'fa-cogs', label: t('operations'), requiresPremium: true },
                                { id: 'cancellations', icon: 'fa-ban', label: t('cancellationsLoss') || 'Cancellations & Loss', requiresPremium: true },
                                { id: 'reports', icon: 'fa-file-alt', label: 'Reports', requiresPremium: true }
                            ];

                            const canAccessPremium = planFeatures.isPremium || planFeatures.isTrial;

                            return tabs.map(tab => {
                                const blocked = tab.requiresPremium && !canAccessPremium;
                                return (
                                    <button
                                        key={tab.id}
                                        onClick={() => blocked ? handleUpgradeClick() : setActiveTab(tab.id)}
                                        className={`flex-shrink-0 min-w-[110px] md:flex-1 md:min-w-[120px] py-3 md:py-4 px-3 md:px-4 flex items-center justify-center gap-2 md:gap-2.5 transition-all relative ${activeTab === tab.id
                                            ? 'text-emerald-600 bg-emerald-50/30'
                                            : blocked ? 'text-gray-300 cursor-not-allowed opacity-60' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                                            }`}
                                        title={blocked ? 'Upgrade to Premium to access this report' : ''}
                                    >
                                        <i className={`fas ${tab.icon} text-xs md:text-sm ${activeTab === tab.id ? 'scale-110' : (blocked ? 'opacity-40' : 'opacity-60')}`}></i>
                                        <span className={`text-[11px] md:text-sm font-bold tracking-tight ${activeTab === tab.id ? '' : 'opacity-80'}`}>{tab.label}</span>
                                        {blocked && <i className="fas fa-lock text-xs ml-2"></i>}
                                        {activeTab === tab.id && <div className="absolute bottom-0 left-0 right-0 h-1 bg-emerald-600 rounded-t-full"></div>}
                                    </button>
                                );
                            });
                        })()}
                    </div>
                </div>

                {error && renderErrorState()}

                {/* Show loader if initial load */}
                {loading && !analyticsData && renderLoadingState()}

                {/* Keep content mounted if we have data, even if loading new data */}
                {analyticsData && (
                    <div id="analytics-dashboard-content" className={`transition-opacity duration-200 ${loading ? 'opacity-50 pointer-events-none relative' : ''}`}>
                        {loading && (
                            <div className="absolute inset-0 z-10 flex items-center justify-center">
                                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-600"></div>
                            </div>
                        )}

                        {/* Overview Tab */}
                        {activeTab === 'overview' && (
                            <>
                                {/* Mobile: Scroll indicator for metrics */}
                                <div className="md:hidden mb-2 px-3">
                                    <p className="text-[9px] font-bold text-gray-500 text-center flex items-center justify-center gap-1">
                                        <i className="fas fa-arrows-alt-h text-[8px]"></i>
                                        Scroll to see all metrics
                                    </p>
                                </div>
                                
                                <div id="metrics-pulse-strip" className="bg-white rounded-2xl md:rounded-[2rem] shadow-xl border border-gray-100 p-2 md:p-2 mb-8 flex flex-row flex-nowrap overflow-x-auto items-stretch divide-x divide-gray-50 expand-in overflow-visible" style={{ animationDelay: '0.1s', scrollbarWidth: 'thin', scrollbarColor: '#10b981 #f3f4f6' }}>
                                    {[
                                        { title: t('totalOrders'), value: analyticsData?.summary.total_orders || 0, icon: 'fa-shopping-cart', trend: analyticsData?.summary.total_orders, prev: previousPeriodData?.total_orders, color: 'emerald' },
                                        { title: t('totalRevenue'), value: formatCurrency(analyticsData?.summary[`total_revenue_${currency.toLowerCase()}`] || 0, currency), icon: 'fa-dollar-sign', trend: analyticsData?.summary[`total_revenue_${currency.toLowerCase()}`], prev: previousPeriodData?.[`total_revenue_${currency.toLowerCase()}`], color: 'teal' },
                                        { title: t('tablesServed'), value: analyticsData?.summary.tables_served || 0, icon: 'fa-chair', trend: analyticsData?.summary.tables_served, prev: previousPeriodData?.tables_served, color: 'emerald' },
                                        { title: t('avgOrderValue'), value: formatCurrency(analyticsData?.summary.avg_order_value || 0, currency), icon: 'fa-receipt', trend: analyticsData?.summary.avg_order_value, prev: previousPeriodData?.avg_order_value, color: 'teal' }
                                    ].map((metric, idx) => {
                                        const change = metric.prev !== undefined ? calculatePercentageChange(metric.trend, metric.prev) : null;
                                        return (
                                            <div key={idx} className="flex-shrink-0 w-[160px] md:flex-1 md:min-w-[180px] md:w-auto px-3 md:px-6 py-3 md:py-4 flex items-start gap-2 md:gap-4 hover:bg-gray-50/50 transition-colors first:rounded-l-2xl md:first:rounded-l-[1.8rem] last:rounded-r-2xl md:last:rounded-r-[1.8rem] overflow-visible">
                                                <div className={`w-9 h-9 md:w-10 md:h-10 rounded-lg md:rounded-xl bg-${metric.color}-50 flex items-center justify-center text-${metric.color}-600 flex-shrink-0 shadow-sm border border-${metric.color}-100 mt-1`}>
                                                    <i className={`fas ${metric.icon} text-xs md:text-sm`}></i>
                                                </div>
                                                <div className="flex flex-col min-w-0 overflow-visible">
                                                    <span className="text-sm font-semibold text-gray-500 leading-normal mb-1 md:mb-1.5 whitespace-nowrap">{metric.title}</span>
                                                    <span className="text-2xl md:text-3xl font-bold text-gray-900 tracking-tight whitespace-nowrap leading-none pb-1">{metric.value}</span>
                                                    <div className="flex items-center min-h-[20px] overflow-visible">
                                                        {change !== null && (
                                                            <span className={`text-[10px] md:text-xs font-bold px-1 md:px-1.5 py-0.5 rounded whitespace-nowrap leading-normal ${parseFloat(change) > 0 ? 'bg-emerald-50 text-emerald-600' : parseFloat(change) < 0 ? 'bg-rose-50 text-rose-600' : 'bg-gray-50 text-gray-400'}`}>
                                                                {parseFloat(change) > 0 ? '+' : ''}{change}%
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>

                                <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 mb-6 expand-in" style={{ animationDelay: '0.2s' }}>
                                    <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center analytics-section-title">
                                        <i className="fas fa-lightbulb text-yellow-500 mr-2"></i>
                                        {t('businessInsights')}
                                    </h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {generateInsights().map((insight, index) => (
                                            <div key={index} className={`insight-card p-4 rounded-lg ${insight.type === 'positive' ? 'positive' : insight.type === 'warning' ? 'negative' : ''}`}>
                                                <div className="flex items-start">
                                                    <i className={`fas ${insight.icon} mr-3 mt-1 ${insight.type === 'positive' ? 'text-green-500' : insight.type === 'warning' ? 'text-yellow-500' : 'text-blue-500'}`}></i>
                                                    <div>
                                                        <h4 className="font-semibold text-gray-900">{insight.title}</h4>
                                                        <p className="text-sm text-gray-600 mt-1">{insight.description}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                                    <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 expand-in chart-container-wrapper analytics-section-card" style={{ animationDelay: '0.3s' }}>
                                        <div className="flex justify-between items-center mb-6">
                                            <h3 className="text-xl font-bold text-gray-900 analytics-section-title">{t('revenueTrend')}</h3>
                                            <div className="flex gap-2">
                                                <button onClick={() => downloadChartData(analyticsData.revenueOrders, 'Revenue_Trend_Data')} className="text-gray-400 hover:text-green-600 transition" title="Download Excel">
                                                    <i className="fas fa-file-excel"></i>
                                                </button>
                                                <button onClick={() => downloadChart(revenueChartRef, `revenue-trend-${timePeriod}-${currency}.png`)} className="text-gray-400 hover:text-blue-600 transition" title="Download Image">
                                                    <i className="fas fa-image"></i>
                                                </button>
                                            </div>
                                        </div>
                                        <div className="chart-container mb-8 analytics-chart-wrapper">
                                            {analyticsData?.revenueOrders?.length > 0 ? (
                                                <canvas ref={revenueChartRef}></canvas>
                                            ) : (
                                                renderNoData()
                                            )}
                                        </div>
                                    </div>
                                    <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 expand-in chart-container-wrapper analytics-section-card" style={{ animationDelay: '0.4s' }}>
                                        <div className="flex justify-between items-center mb-6">
                                            <h3 className="text-xl font-bold text-gray-900 analytics-section-title">{t('ordersOverview')}</h3>
                                            <div className="flex gap-2">
                                                <button onClick={() => downloadChartData(analyticsData.revenueOrders, 'Orders_Overview_Data')} className="text-gray-400 hover:text-green-600 transition" title="Download Excel">
                                                    <i className="fas fa-file-excel"></i>
                                                </button>
                                                <button onClick={() => downloadChart(ordersChartRef, `orders-overview-${timePeriod}.png`)} className="text-gray-400 hover:text-blue-600 transition" title="Download Image">
                                                    <i className="fas fa-image"></i>
                                                </button>
                                            </div>
                                        </div>
                                        <div className="chart-container mb-8 analytics-chart-wrapper">
                                            {analyticsData?.revenueOrders?.length > 0 ? (
                                                <canvas ref={ordersChartRef}></canvas>
                                            ) : (
                                                renderNoData()
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Branch Comparison Section */}
                                {branchComparisonData && (
                                    <div className="mb-6 expand-in" style={{ animationDelay: '0.5s' }}>
                                        <h3 className="text-xl font-bold text-gray-900 mb-4 analytics-section-title flex items-center">
                                            <i className="fas fa-sitemap mr-2 text-emerald-600"></i>
                                            {t('branchComparison') || "Branch Comparison"}
                                        </h3>
                                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                            {/* Revenue by Branch */}
                                            <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 chart-container-wrapper analytics-section-card">
                                                <h4 className="text-md font-bold text-gray-800 mb-4">{t('revenueByBranch') || "Revenue by Branch"}</h4>
                                                <div className="chart-container h-80 analytics-chart-wrapper">
                                                    <canvas ref={branchRevenueChartRef}></canvas>
                                                </div>
                                            </div>
                                            {/* Orders by Branch */}
                                            <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 chart-container-wrapper analytics-section-card">
                                                <h4 className="text-md font-bold text-gray-800 mb-4">{t('ordersByBranch') || "Orders by Branch"}</h4>
                                                <div className="chart-container h-80 analytics-chart-wrapper">
                                                    <canvas ref={branchOrdersChartRef}></canvas>
                                                </div>
                                            </div>
                                        </div>
                                        {/* Top Items by Branch */}
                                        <div className="mt-6">
                                            <h4 className="text-md font-bold text-gray-800 mb-4">{t('topItemsByBranch') || "Top Items by Branch"}</h4>
                                            {branchComparisonData.topItems && branchComparisonData.topItems.length > 0 ? (
                                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                                    {branchComparisonData.topItems.map((branchData, idx) => (
                                                        <div key={idx} className="border border-gray-100 rounded-lg p-4 bg-gray-50">
                                                            <h5 className="font-bold text-indigo-900 mb-2 border-b pb-2">{branchData.branch_name}</h5>
                                                            <ul className="space-y-2">
                                                                {branchData.items.map((item, i) => (
                                                                    <li key={i} className="flex justify-between text-sm">
                                                                        <span className="text-gray-700 truncate pr-2" title={item.name}>{item.name}</span>
                                                                        <span className="font-semibold text-gray-900">{item.quantity}</span>
                                                                    </li>
                                                                ))}
                                                                {branchData.items.length === 0 && <li className="text-red-600 font-normal text-xs text-center">No items sold</li>}
                                                            </ul>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className="text-center text-red-600 font-normal py-4">No item data available</div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </>
                        )}

                        {/* Revenue Tab */}
                        {activeTab === 'revenue' && (
                            <>
                                <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 mb-6 expand-in analytics-section-card" style={{ animationDelay: '0.1s' }}>
                                    <h3 className="text-xl font-bold text-gray-900 mb-8 analytics-section-title">{t('revenueBreakdown')}</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                        <div>
                                            <div className="flex justify-between items-center mb-6">
                                                <h4 className="text-md font-semibold text-gray-800">{t('revenueByCategory')}</h4>
                                                <div className="flex gap-2">
                                                    <button onClick={() => downloadChartData(analyticsData.categories, 'Revenue_by_Category_Data')} className="text-gray-400 hover:text-green-600 transition" title="Download Excel">
                                                        <i className="fas fa-file-excel"></i>
                                                    </button>
                                                    <button onClick={() => downloadChart(categoryChartRef, `revenue-by-category-${timePeriod}-${currency}.png`)} className="text-gray-400 hover:text-blue-600 transition" title="Download Image">
                                                        <i className="fas fa-image"></i>
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="chart-container mb-4">
                                                {analyticsData?.categories?.length > 0 ? (
                                                    <canvas ref={categoryChartRef}></canvas>
                                                ) : (
                                                    renderNoData()
                                                )}
                                            </div>
                                        </div>
                                        <div>
                                            <div className="flex justify-between items-center mb-6">
                                                <h4 className="text-md font-semibold text-gray-800">{t('paymentMethods')}</h4>
                                                <div className="flex gap-2">
                                                    <button onClick={() => downloadChartData(analyticsData.paymentMethods, 'Payment_Methods_Data')} className="text-gray-400 hover:text-green-600 transition" title="Download Excel">
                                                        <i className="fas fa-file-excel"></i>
                                                    </button>
                                                    <button onClick={() => downloadChart(paymentChartRef, `payment-methods-${timePeriod}.png`)} className="text-gray-400 hover:text-blue-600 transition" title="Download Image">
                                                        <i className="fas fa-image"></i>
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="chart-container mb-4">
                                                {analyticsData?.paymentMethods && Object.keys(analyticsData.paymentMethods).length > 0 ? (
                                                    <canvas ref={paymentChartRef}></canvas>
                                                ) : (
                                                    renderNoData()
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="mt-12">
                                        <div className="flex justify-between items-center mb-6">
                                            <h4 className="text-md font-semibold text-gray-800">{t('revenueTrend')}</h4>
                                            <div className="flex gap-2">
                                                <button onClick={() => downloadChartData(analyticsData.revenueOrders, 'Revenue_Trend_Data')} className="text-gray-400 hover:text-green-600 transition" title="Download Excel">
                                                    <i className="fas fa-file-excel"></i>
                                                </button>
                                                <button onClick={() => downloadChart(revenueChartRef, `revenue-trend-${timePeriod}-${currency}.png`)} className="text-gray-400 hover:text-blue-600 transition" title="Download Image">
                                                    <i className="fas fa-image"></i>
                                                </button>
                                            </div>
                                        </div>
                                        <div className="chart-container mb-8 analytics-chart-wrapper">
                                            {analyticsData?.revenueOrders?.length > 0 ? (
                                                <canvas ref={revenueChartRef}></canvas>
                                            ) : (
                                                renderNoData()
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}

                        {/* Items Tab */}
                        {activeTab === 'items' && (
                            <>
                                <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 mb-6 expand-in analytics-section-card" style={{ animationDelay: '0.1s' }}>
                                    <h3 className="text-xl font-bold text-gray-900 mb-8 analytics-section-title">{t('itemPerformance')}</h3>
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                        <div>
                                            <div className="flex justify-between items-center mb-6">
                                                <h4 className="text-md font-semibold text-gray-800">{t('topSellingItems')}</h4>
                                                <div className="flex gap-2">
                                                    <button onClick={() => downloadChartData(analyticsData.topItems, 'Top_Items_Data')} className="text-gray-400 hover:text-green-600 transition" title="Download Excel">
                                                        <i className="fas fa-file-excel"></i>
                                                    </button>
                                                    <button onClick={() => downloadChart(itemsChartRef, `top-items-${timePeriod}-${currency}.png`)} className="text-gray-400 hover:text-blue-600 transition" title={t('downloadImage') || "Download Image"}>
                                                        <i className="fas fa-image"></i>
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="chart-container mb-4">
                                                {analyticsData?.topItems?.length > 0 ? (
                                                    <canvas ref={itemsChartRef}></canvas>
                                                ) : (
                                                    renderNoData()
                                                )}
                                            </div>
                                        </div>
                                        <div>
                                            <div className="flex justify-between items-center mb-6">
                                                <h4 className="text-md font-semibold text-gray-800">{t('categoryPerformance')}</h4>
                                                <div className="flex gap-2">
                                                    <button onClick={() => downloadChartData(analyticsData.categories, 'Category_Performance_Data')} className="text-gray-400 hover:text-green-600 transition" title="Download Excel">
                                                        <i className="fas fa-file-excel"></i>
                                                    </button>
                                                    <button onClick={() => downloadChart(categoryChartRef, `category-performance-${timePeriod}-${currency}.png`)} className="text-gray-400 hover:text-blue-600 transition" title="Download Image">
                                                        <i className="fas fa-image"></i>
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="chart-container mb-4">
                                                {analyticsData?.categories?.length > 0 ? (
                                                    <canvas ref={categoryChartRef}></canvas>
                                                ) : (
                                                    renderNoData()
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="mt-12">
                                        <h4 className="text-md font-semibold text-gray-800 mb-6">{t('topItemsDetails')}</h4>
                                        <div className="space-y-3 max-h-80 overflow-y-auto custom-scrollbar">
                                            {analyticsData?.topItems?.length > 0 ? (
                                                analyticsData.topItems.map((item, index) => (
                                                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition">
                                                        <div className="flex items-center">
                                                            <span className="text-lg font-bold text-gray-500 mr-3">{index + 1}</span>
                                                            <div>
                                                                <p className="font-semibold text-gray-900">{t(item.item_name)}</p>
                                                                {language !== 'en' && item.item_name && t(item.item_name) !== item.item_name && (
                                                                    <p className="text-[10px] text-gray-400 -mt-1">{item.item_name}</p>
                                                                )}
                                                                <p className="text-xs text-gray-500">{t(item.category)}</p>
                                                            </div>
                                                        </div>
                                                        <div className="text-right">
                                                            <p className="font-bold text-orange-600">
                                                                {formatCurrency(item[`revenue_${currency.toLowerCase()}`] || 0, currency)}
                                                            </p>
                                                            <p className="text-xs text-gray-500">{item.quantity_sold || 0} items</p>
                                                        </div>
                                                    </div>
                                                ))
                                            ) : (
                                                renderNoData(t('noItemsSold') || "No items sold in this period")
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}

                        {/* Discounts Tab */}
                        {activeTab === 'discounts' && (
                            <>
                                <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 mb-6 expand-in analytics-section-card" style={{ animationDelay: '0.1s' }}>
                                    <h3 className="text-xl font-bold text-gray-900 mb-8 analytics-section-title">
                                        <i className="fas fa-percentage text-emerald-600 mr-2"></i>
                                        {t('discountsPromos') || 'Discounts & Promos'}
                                    </h3>
                                    
                                    {/* Metrics cards */}
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                                        <div className="p-5 bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl border border-emerald-200">
                                            <p className="text-sm font-semibold text-emerald-700 mb-1">{t('totalDiscountAmount') || 'Total Discount Given'}</p>
                                            <p className="text-3xl font-black text-emerald-950">
                                                {formatAmount(
                                                    analyticsData.discounts?.summary?.[currency === 'INR' ? 'total_discount_inr' : 'total_discount_usd'] || 0
                                                )}
                                            </p>
                                        </div>
                                        <div className="p-5 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl border border-blue-200">
                                            <p className="text-sm font-semibold text-blue-700 mb-1">{t('ordersWithDiscount') || 'Orders with Discount'}</p>
                                            <p className="text-3xl font-black text-blue-950">
                                                {analyticsData.discounts?.summary?.orders_with_discount || 0}
                                            </p>
                                        </div>
                                        <div className="p-5 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl border border-purple-200">
                                            <p className="text-sm font-semibold text-purple-700 mb-1">{t('avgDiscountPercentage') || 'Avg Discount Percentage'}</p>
                                            <p className="text-3xl font-black text-purple-950">
                                                {parseFloat(analyticsData.discounts?.summary?.avg_discount_percentage || 0).toFixed(1)}%
                                            </p>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                        <div>
                                            <div className="flex justify-between items-center mb-6">
                                                <h4 className="text-md font-semibold text-gray-800">{t('discountChartTitle') || 'Top Discounted Items by Loss'}</h4>
                                                <div className="flex gap-2">
                                                    <button onClick={() => downloadChartData(analyticsData.discounts?.items || [], 'Discount_Data')} className="text-gray-400 hover:text-green-600 transition" title="Download Excel">
                                                        <i className="fas fa-file-excel"></i>
                                                    </button>
                                                    <button onClick={() => downloadChart(customerChartRef, `discount-performance-${timePeriod}-${currency}.png`)} className="text-gray-400 hover:text-blue-600 transition" title="Download Image">
                                                        <i className="fas fa-image"></i>
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="chart-container mb-4">
                                                {analyticsData.discounts?.items?.length > 0 ? (
                                                    <canvas ref={customerChartRef}></canvas>
                                                ) : (
                                                    renderNoData()
                                                )}
                                            </div>
                                        </div>
                                        <div>
                                            <h4 className="text-md font-semibold text-gray-800 mb-3">{t('discountItemsTable') || 'Discount Details'}</h4>
                                            <div className="overflow-x-auto">
                                                <table className="w-full text-left">
                                                    <thead className="bg-gray-50 border-b border-gray-100">
                                                        <tr>
                                                            <th className="p-3 font-semibold text-gray-600 text-xs">{t('itemName') || 'Item'}</th>
                                                            <th className="p-3 text-center font-semibold text-gray-600 text-xs">{t('discountPercentage') || 'Discount %'}</th>
                                                            <th className="p-3 text-center font-semibold text-gray-600 text-xs">{t('quantity') || 'Qty'}</th>
                                                            <th className="p-3 text-right font-semibold text-gray-600 text-xs">{t('totalDiscount') || 'Total Discount'}</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-gray-100 text-xs">
                                                        {(analyticsData.discounts?.items || []).map((row, idx) => (
                                                            <tr key={idx} className="hover:bg-gray-50 transition">
                                                                <td className="py-3 font-medium text-gray-900">{t(row.item_name)}</td>
                                                                <td className="py-3 text-center font-bold text-red-600">{row.discount_percentage}%</td>
                                                                <td className="py-3 text-center">{row.total_quantity}</td>
                                                                <td className="py-3 text-right font-bold text-gray-900">
                                                                    {formatAmount(currency === 'INR' ? row.total_discount_inr : row.total_discount_usd)}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                            {(!analyticsData.discounts?.items || analyticsData.discounts.items.length === 0) && renderNoData()}
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}

                        {/* Operations Tab */}
                        {activeTab === 'operations' && (
                            <>
                                <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 mb-6 expand-in analytics-section-card" style={{ animationDelay: '0.1s' }}>
                                    <h3 className="text-xl font-bold text-gray-900 mb-8 analytics-section-title">{t('operationsAnalysis')}</h3>
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                        <div>
                                            <div className="flex justify-between items-center mb-6">
                                                <h4 className="text-md font-semibold text-gray-800">{t('hourlyOrderDistribution')}</h4>
                                                <div className="flex gap-2">
                                                    <button onClick={() => downloadChartData(analyticsData.hourlyOrders, 'Hourly_Orders_Data')} className="text-gray-400 hover:text-green-600 transition" title="Download Excel">
                                                        <i className="fas fa-file-excel"></i>
                                                    </button>
                                                    <button onClick={() => downloadChart(hourlyChartRef, `hourly-orders-${timePeriod}-${currency}.png`)} className="text-gray-400 hover:text-blue-600 transition" title="Download Image">
                                                        <i className="fas fa-image"></i>
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="chart-container mb-4">
                                                {analyticsData?.hourlyOrders?.length > 0 ? (
                                                    <canvas ref={hourlyChartRef}></canvas>
                                                ) : (
                                                    renderNoData()
                                                )}
                                            </div>
                                        </div>
                                        <div>
                                            <div className="flex justify-between items-center mb-6">
                                                <h4 className="text-md font-semibold text-gray-800">{t('tablePerformance')}</h4>
                                                <div className="flex gap-2">
                                                    <button onClick={() => downloadChartData(analyticsData.tablePerformance, 'Table_Performance_Data')} className="text-gray-400 hover:text-green-600 transition" title="Download Excel">
                                                        <i className="fas fa-file-excel"></i>
                                                    </button>
                                                    <button onClick={() => downloadChart(tableChartRef, `table-performance-${timePeriod}-${currency}.png`)} className="text-gray-400 hover:text-blue-600 transition" title="Download Image">
                                                        <i className="fas fa-image"></i>
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="chart-container mb-4">
                                                {analyticsData?.tablePerformance?.length > 0 ? (
                                                    <canvas ref={tableChartRef}></canvas>
                                                ) : (
                                                    renderNoData()
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="mt-12">
                                        <h4 className="text-md font-semibold text-gray-800 mb-6">{t('tablePerformanceDetails')}</h4>
                                        <div className="overflow-x-auto">
                                            {analyticsData?.tablePerformance?.length > 0 ? (
                                                <table className="w-full text-left">
                                                    <thead className="bg-gray-50 border-b border-gray-100">
                                                        <tr>
                                                            <th className="p-4 font-semibold text-gray-600">{t('table')}</th>
                                                            <th className="p-4 font-semibold text-gray-600">{t('orders')}</th>
                                                            <th className="p-4 font-semibold text-gray-600">{t('totalRevenue')}</th>
                                                            <th className="p-4 font-semibold text-gray-600">{t('avgOrderValue')}</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-gray-100 text-sm">
                                                        {analyticsData?.tablePerformance.map((table, index) => (
                                                            <tr key={index} className="hover:bg-gray-50 transition">
                                                                <td className="p-4 font-medium text-gray-900">
                                                                    <div>{t(table.table_name)}</div>
                                                                    {language !== 'en' && table.table_name && t(table.table_name) !== table.table_name && (
                                                                        <div className="text-[10px] text-gray-400 font-normal">{table.table_name}</div>
                                                                    )}
                                                                </td>
                                                                <td className="p-4 text-gray-600">{table.total_orders}</td>
                                                                <td className="p-4 text-gray-800 font-medium">{formatCurrency(table[`total_revenue_${currency.toLowerCase()}`] || 0, currency)}</td>
                                                                <td className="p-4 text-gray-800 font-medium">{formatCurrency(table[`avg_order_value_${currency.toLowerCase()}`] || 0, currency)}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            ) : (
                                                renderNoData(t('noTableData') || "No table data available")
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}

                        {/* Cancellations Tab */}
                        {activeTab === 'cancellations' && (
                            canAccessPremium ? (
                                <div className="bg-white rounded-xl shadow-lg p-6 expand-in analytics-section-card" style={{ animationDelay: '0.1s' }}>
                                    <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center analytics-section-title">
                                        <i className="fas fa-ban text-emerald-600 mr-2"></i>
                                        {t('cancellationsLoss') || 'Cancellations & Loss'}
                                    </h3>

                                    {/* Metrics strip */}
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                                        <div className="p-5 bg-gradient-to-br from-rose-50 to-rose-100 rounded-xl border border-rose-200">
                                            <p className="text-sm font-semibold text-rose-700 mb-1">{t('totalRevenueLost') || 'Total Revenue Lost'}</p>
                                            <p className="text-3xl font-black text-rose-950">
                                                {formatAmount(
                                                    analyticsData.cancellations?.cancelledItems?.reduce((acc, item) => acc + (currency === 'INR' ? parseFloat(item.lost_inr || 0) : parseFloat(item.lost_usd || 0)), 0) || 0
                                                )}
                                            </p>
                                        </div>
                                        <div className="p-5 bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl border border-amber-200">
                                            <p className="text-sm font-semibold text-amber-700 mb-1">{t('cancelledOrdersCount') || 'Cancelled Orders'}</p>
                                            <p className="text-3xl font-black text-amber-950">
                                                {analyticsData.cancellations?.statusStats?.find(s => s.order_status === 'cancelled')?.count || 0}
                                            </p>
                                        </div>
                                        <div className="p-5 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border border-gray-200">
                                            <p className="text-sm font-semibold text-gray-700 mb-1">{t('cancelledItemsCount') || 'Cancelled Items Count'}</p>
                                            <p className="text-3xl font-black text-gray-950">
                                                {analyticsData.cancellations?.cancelledItems?.reduce((acc, item) => acc + parseInt(item.quantity || 0), 0) || 0}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                        {/* Left table: Cancelled Items */}
                                        <div>
                                            <h4 className="text-md font-semibold text-gray-800 mb-4">{t('topCancelledItems') || 'Most Cancelled Menu Items'}</h4>
                                            <div className="overflow-x-auto">
                                                <table className="w-full text-left">
                                                    <thead className="bg-gray-50 border-b border-gray-100">
                                                        <tr>
                                                            <th className="p-3 font-semibold text-gray-600 text-xs">{t('itemName') || 'Item'}</th>
                                                            <th className="p-3 text-center font-semibold text-gray-600 text-xs">{t('quantity') || 'Quantity'}</th>
                                                            <th className="p-3 text-right font-semibold text-gray-600 text-xs">{t('estimatedLoss') || 'Loss amount'}</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-gray-100 text-xs">
                                                        {(analyticsData.cancellations?.cancelledItems || []).map((row, idx) => (
                                                            <tr key={idx} className="hover:bg-gray-50 transition">
                                                                <td className="py-3 font-medium text-gray-900">{t(row.item_name)}</td>
                                                                <td className="py-3 text-center font-bold text-red-500">{row.quantity}</td>
                                                                <td className="py-3 text-right font-bold text-rose-600">
                                                                    {formatAmount(currency === 'INR' ? row.lost_inr : row.lost_usd)}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                            {(!analyticsData.cancellations?.cancelledItems || analyticsData.cancellations.cancelledItems.length === 0) && renderNoData()}
                                        </div>

                                        {/* Right table: Overall Status Stats */}
                                        <div>
                                            <h4 className="text-md font-semibold text-gray-800 mb-4">{t('orderStatusOverview') || 'Order Status Overview'}</h4>
                                            <div className="overflow-x-auto">
                                                <table className="w-full text-left">
                                                    <thead className="bg-gray-50 border-b border-gray-100">
                                                        <tr>
                                                            <th className="p-3 font-semibold text-gray-600 text-xs">{t('orderStatus') || 'Status'}</th>
                                                            <th className="p-3 text-center font-semibold text-gray-600 text-xs">{t('totalOrders') || 'Orders Count'}</th>
                                                            <th className="p-3 text-right font-semibold text-gray-600 text-xs">{t('revenue') || 'Total Revenue'}</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-gray-100 text-xs">
                                                        {(analyticsData.cancellations?.statusStats || []).map((row, idx) => {
                                                            const isCancelled = row.order_status === 'cancelled';
                                                            const isCompleted = row.order_status === 'completed' || row.order_status === 'delivered';
                                                            return (
                                                                <tr key={idx} className={`hover:bg-gray-50 transition ${isCancelled ? 'bg-rose-50/30' : ''}`}>
                                                                    <td className="py-3 capitalize">
                                                                        <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold ${
                                                                            isCancelled ? 'bg-rose-100 text-rose-700' :
                                                                            isCompleted ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                                                                        }`}>
                                                                            {t(row.order_status) || row.order_status}
                                                                        </span>
                                                                    </td>
                                                                    <td className="py-3 text-center font-bold">{row.count}</td>
                                                                    <td className="py-3 text-right font-bold text-gray-900">
                                                                        {formatAmount(currency === 'INR' ? row.total_inr : row.total_usd)}
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                            {(!analyticsData.cancellations?.statusStats || analyticsData.cancellations.statusStats.length === 0) && renderNoData()}
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="p-10 bg-white rounded-2xl border border-gray-100 text-center">
                                    <div className="text-emerald-600 mb-4 text-4xl"><i className="fas fa-lock"></i></div>
                                    <h3 className="text-xl font-bold mb-2">Reports are Premium</h3>
                                    <p className="text-gray-600 mb-6">Exportable reports, cancellations analysis and AI suggestions require the Premium plan. Upgrade to unlock full reporting capabilities.</p>
                                    <button onClick={handleUpgradeClick} className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-xl font-bold">Upgrade to Premium</button>
                                </div>
                            )
                        )}

                        {/* Reports Tab */}
                        {activeTab === 'reports' && (
                                <div className="space-y-8 expand-in" style={{ animationDelay: '0.1s' }}>
                                {/* Info Banner */}
                                <div className="bg-gradient-to-r from-emerald-500/10 to-teal-500/10 backdrop-blur-md rounded-2xl p-6 border border-emerald-100 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                                    <div>
                                        <h3 className="text-xl font-extrabold text-gray-900 mb-1 flex items-center">
                                            <i className="fas fa-file-invoice text-emerald-600 mr-2"></i>
                                            <span>Executive Report Center & AI Insights</span>
                                        </h3>
                                        <p className="text-sm text-gray-600">
                                            Download 10 custom-tailored, detailed spreadsheets detailing sales, tax billing, hourly performance, inventory stock status, and staff metrics.
                                        </p>
                                    </div>
                                    <span className="text-xs font-bold text-purple-700 bg-purple-100 border border-purple-200 px-3 py-1 rounded-full whitespace-nowrap">
                                        <i className="fas fa-robot mr-1"></i> AI Suggestions Enabled
                                    </span>
                                </div>

                                {/* Premium Sub-Tab Pill Selector */}
                                <div className="flex justify-center">
                                    <div className="flex bg-gray-100/80 backdrop-blur-md rounded-2xl p-1.5 shadow-inner border border-gray-200/50 max-w-md w-full">
                                        <button
                                            type="button"
                                            onClick={() => setReportsSubSection('excel')}
                                            className={`flex-1 py-3 px-5 rounded-xl text-sm font-black transition-all duration-300 flex items-center justify-center gap-2 ${
                                                reportsSubSection === 'excel'
                                                    ? 'bg-white text-emerald-600 shadow-md transform scale-105 border border-gray-100'
                                                    : 'text-gray-500 hover:text-gray-800'
                                            }`}
                                        >
                                            <i className="fas fa-file-excel"></i>
                                            <span>Excel Reports</span>
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setReportsSubSection('ai')}
                                            className={`flex-1 py-3 px-5 rounded-xl text-sm font-black transition-all duration-300 flex items-center justify-center gap-2 ${
                                                reportsSubSection === 'ai'
                                                    ? 'bg-white text-purple-600 shadow-md transform scale-105 border border-gray-100'
                                                    : 'text-gray-500 hover:text-gray-800'
                                            }`}
                                        >
                                            <i className="fas fa-brain"></i>
                                            <span>AI Suggestions</span>
                                        </button>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                                    {/* Left Column: 10 Reports */}
                                    {reportsSubSection === 'excel' && (
                                        <div className="lg:col-span-12 w-full bg-white rounded-2xl shadow-sm p-6 md:p-8 border border-gray-200 transition-all duration-300">
                                            <h4 className="text-lg font-bold text-gray-800 mb-6 flex items-center gap-2.5">
                                                <i className="fas fa-file-excel text-emerald-600 text-xl"></i>
                                                <span>Restaurant Excel Report Suite</span>
                                            </h4>
                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                                {[
                                                    { id: 'branch_summary', title: 'Branch Order & Revenue', desc: 'Summary of order counts, gross sales, CGST/SGST, and net sales per branch.' },
                                                    { id: 'staff_performance', title: 'Staff Shift & Payout', desc: 'Detailed report of present, absent, leaves, and salary computed.' },
                                                    { id: 'inventory_usage', title: 'Inventory Stock Audit', desc: 'Current ingredient stock levels, values, and status warnings.' },
                                                    { id: 'hourly_sales', title: 'Hourly Sales & Peak Slots', desc: 'Analysis of order counts and traffic per hourly window.' },
                                                    { id: 'top_selling', title: 'Top-Selling Items Contribution', desc: 'Ranking of items by quantity sold and revenue share.' },
                                                    { id: 'payment_methods', title: 'Payment Type Share Analysis', desc: 'Distribution of UPI, Card, and Cash billing methods.' },
                                                    { id: 'discount_audit', title: 'Discount & Cancel Audit', desc: 'Log of discounts applied and cancelled items loss.' },
                                                    { id: 'customer_feedback', title: 'Customer Feedback & Ratings', desc: 'Recent order ratings, reviews, and follow-ups.' },
                                                    { id: 'table_turnaround', title: 'Table Turnaround & Revenue', desc: 'Average occupancy times and revenue per dining table.' },
                                                    { id: 'tax_billing', title: 'Tax & SGST/CGST Summary', desc: 'Taxes collected, service charge, and gross net details.' }
                                                ].map((rep, idx) => (
                                                    <div key={idx} className="p-5 rounded-2xl border border-gray-100 hover:border-emerald-300 hover:shadow-lg transition-all flex flex-col justify-between bg-gray-50/50 hover:bg-white">
                                                        <div>
                                                            <h5 className="font-bold text-sm text-gray-900 mb-1.5">{rep.title}</h5>
                                                            <p className="text-xs text-gray-500 leading-relaxed mb-4">{rep.desc}</p>
                                                        </div>
                                                        <button
                                                            onClick={() => downloadReport(rep.id)}
                                                            className="w-full py-2.5 bg-white hover:bg-emerald-50 text-emerald-600 border border-emerald-200 rounded-xl text-xs font-bold transition flex items-center justify-center gap-2 active:scale-95 shadow-sm"
                                                        >
                                                            <i className="fas fa-file-download text-xs"></i>
                                                            <span>Download Excel</span>
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Right Column: AI Insights */}
                                    {reportsSubSection === 'ai' && (
                                        <div className="lg:col-span-12 w-full bg-white rounded-2xl shadow-sm p-6 md:p-8 border border-gray-200 transition-all duration-300">
                                            <h4 className="text-lg font-bold text-gray-800 mb-6 flex items-center gap-2.5">
                                                <i className="fas fa-brain text-purple-600 text-xl"></i>
                                                <span>AI Suggestions & Insights</span>
                                            </h4>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                {generateAISuggestions().map((insight, idx) => (
                                                    <div key={idx} className={`p-5 rounded-2xl border flex gap-4 ${
                                                        insight.color === 'amber' ? 'bg-amber-50/40 border-amber-200 text-amber-900' :
                                                        insight.color === 'emerald' ? 'bg-emerald-50/40 border-emerald-200 text-emerald-900' :
                                                        insight.color === 'rose' ? 'bg-rose-50/40 border-rose-200 text-rose-900' :
                                                        insight.color === 'indigo' ? 'bg-indigo-50/40 border-indigo-200 text-indigo-900' :
                                                        'bg-teal-50/40 border-teal-200 text-teal-900'
                                                    }`}>
                                                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5 ${
                                                            insight.color === 'amber' ? 'bg-amber-100 text-amber-600' :
                                                            insight.color === 'emerald' ? 'bg-emerald-100 text-emerald-600' :
                                                            insight.color === 'rose' ? 'bg-rose-100 text-rose-600' :
                                                            insight.color === 'indigo' ? 'bg-indigo-100 text-indigo-600' :
                                                            'bg-teal-100 text-teal-600'
                                                        }`}>
                                                            <i className={`fas ${insight.icon} text-sm`}></i>
                                                        </div>
                                                        <div className="flex-1">
                                                            <div className="flex items-center justify-between mb-1.5">
                                                                <span className="font-bold text-sm">{insight.title}</span>
                                                                <span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${
                                                                    insight.impact === 'High Impact' ? 'bg-rose-100 text-rose-700' : 'bg-blue-100 text-blue-700'
                                                                }`}>{insight.impact}</span>
                                                            </div>
                                                            <p className="text-xs text-gray-600 leading-relaxed">{insight.description}</p>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
            </div>
        </div>
        {confirmModal.show && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => setConfirmModal({ show: false })}>
                    <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6 text-center" onClick={e => e.stopPropagation()}>
                        <div className="w-16 h-16 bg-indigo-50 rounded-full flex items-center justify-center mx-auto mb-4">
                            <i className="fas fa-crown text-2xl text-indigo-500"></i>
                        </div>
                        <h3 className="text-xl font-bold mb-2 text-gray-900">{confirmModal.title || 'Confirm Action'}</h3>
                        <p className="mb-6 text-gray-500 text-sm leading-relaxed">{confirmModal.message}</p>
                        <div className="flex gap-3">
                            <button onClick={() => setConfirmModal({ show: false })} className={confirmModal.cancelClass || "btn btn-secondary flex-1 py-2"}>Cancel</button>
                            <button onClick={confirmModal.onConfirm} className={confirmModal.confirmClass || "btn btn-danger flex-1 py-2"}>{confirmModal.confirmText || 'Yes'}</button>
                        </div>
                    </div>
                </div>
            )}
            
            <PlanRenewalModal
                isOpen={isProratedUpgradeOpen}
            onClose={() => setIsProratedUpgradeOpen(false)}
            companyProfile={companyInfo}
            token={token}
            onSuccess={() => {
                // Re-fetch company info so plan badge updates
                const API_URL_local = window.location.hostname === 'localhost' || window.location.hostname.endsWith('.localhost') || window.location.hostname === '127.0.0.1'
                    ? 'http://localhost:5000'
                    : (process.env.REACT_APP_API_URL || 'https://dineflowbackend.onrender.com');
                fetch(`${API_URL_local}/api/company/profile`, { headers: { Authorization: `Bearer ${token}` } })
                    .then(r => r.json())
                    .then(d => { if (d.success) setCompanyInfo(d.data); })
                    .catch(() => {});
            }}
        />
        </>
    );
}

export default AnalyticsPage;





